<!--
  Origin references for the original to-do implementation:
    https://vuejsexamples.com/simple-todo-vue-app-with-localstorage-persistence/
    https://github.com/dobromir-hristov/todo-app
    https://stackoverflow.com/questions/53899676/vue-2-contenteditable-with-v-model
  2026-09-11: this comment once wrapped an entire style block (the .tag-input width and flex
  rules among others) that therefore never compiled. The composer's sizing lives in
  src/components/TaskComposer/TaskComposer.vue. Never put an SFC block inside a comment —
  cypress/node-tests/task-composer-layout-contract.test.js §1 fails the deploy gate on it.
-->
<template>
  <div>
  <!-- Tasks page redesign (design "Tasks 2a", Phase B1 — plan: .claude/plans/eager-painting-hejlsberg.md).
       Super Admins get the new branch unless they switch; offices follow STAGE.tasks (src/utils/followups/uiVersion.js); the v-else
       branch below is the classic page, unchanged. Both branches read the SAME store lists. -->
  <div v-if="fuUseNewUi" class="tk-page" data-test="tasks-panel">
    <followups-ui-toggle target="old" page-key="tasks" @toggle="fuSetUi" />
    <!-- Task reports (design 1c): a full page inside the Tasks page, ?screen=reports. -->
    <task-reports-page
      v-if="tkScreen === 'reports'"
      :items="tkReportItems"
      :employees="employees || []"
      :site-tags="computedAvailableTags"
      :today-ymd="tkReportToday"
      :busy="tkReportsWaiting"
      @back="tkCloseReports"
    />
    <div v-else class="tk-page__grid" :class="{ 'tk-page__grid--narrow': tkNarrow }">
      <aside class="tk-page__side">
        <tasks-sidebar
          :views="tkViews"
          :view="tkSidebarView"
          :team="tkTeam"
          :tags="tkTagRows"
          :emp="tkEmp"
          :tag="tkTag"
          :q="tkQ"
          :team-shown="tkTeamShown"
          :tags-shown="tkTagsShown"
          :compact="tkNarrow"
          @update:view="tkSetView"
          @update:emp="tkSetEmp"
          @update:tag="tkSetTag"
          @update:q="tkQ = $event"
        />
      </aside>

      <main class="tk-page__main">
        <header class="tk-page__head">
          <div class="tk-page__intro">
            <div class="tk-page__eyebrow" data-test="tasks-eyebrow">{{ tkEyebrow }}</div>
            <h1 class="tk-page__title" data-test="tasks-title">{{ tkTitle }}</h1>
            <p class="tk-page__lede" data-test="tasks-lede">{{ tkLede }}</p>
          </div>
          <div v-if="tkIsMyDay" class="tk-page__progress" data-test="tasks-progress">
            <div class="tk-page__ring" role="img" :aria-label="`${tkProgress.done} of ${tkProgress.total} done today`">
              <svg viewBox="0 0 64 64" width="64" height="64" aria-hidden="true" focusable="false">
                <circle cx="32" cy="32" r="27" class="tk-page__ring-track" />
                <circle
                  cx="32"
                  cy="32"
                  r="27"
                  class="tk-page__ring-fill"
                  transform="rotate(-90 32 32)"
                  :stroke-dasharray="tkRingDash"
                  :stroke-dashoffset="tkRingOffset"
                />
              </svg>
              <span class="tk-page__ring-txt" data-test="tasks-progress-ratio">{{ tkProgress.done }}/{{ tkProgress.total }}</span>
            </div>
            <div class="tk-page__progress-text">
              <div class="tk-page__progress-title" data-test="tasks-progress-title">{{ tkRingTitle }}</div>
              <div v-if="tkRingSub" class="tk-page__progress-sub" data-test="tasks-progress-sub">{{ tkRingSub }}</div>
            </div>
          </div>
          <!-- Design 2a: Reports (full page, 1c) + the settings gear (440px panel). Every view. -->
          <div class="tk-page__actions" data-test="tasks-actions">
            <!-- Super Admin preview: List / Calendar / Workload. Everyone else never sees it (tkLayout is 'list'). -->
            <div v-if="isSuperAdmin" class="tk-page__layouts" role="group" aria-label="Tasks layout" data-test="tasks-layout-switch">
              <button
                v-for="l in tkLayoutChoices"
                :key="l.key"
                type="button"
                class="tk-page__layout"
                :class="{ 'is-on': tkLayout === l.key }"
                :aria-pressed="tkLayout === l.key ? 'true' : 'false'"
                :data-test="`tasks-layout-${l.key}`"
                @click="tkSetLayout(l.key)"
              >{{ l.label }}</button>
            </div>
            <button type="button" class="tk-page__btn" data-test="tasks-reports-btn" @click="tkOpenReports">
              <svg class="tk-page__btn-ic" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 3v18h18" /><path d="M18 17V9M13 17V5M8 17v-3" /></svg>
              Reports
            </button>
            <button
              type="button"
              class="tk-page__icon-btn"
              title="Task settings"
              aria-label="Task settings"
              data-test="tasks-settings-btn"
              @click="tkOpenSettings"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 7h-9M14 17H5" /><circle cx="17" cy="17" r="3" /><circle cx="7" cy="7" r="3" /></svg>
            </button>
          </div>
        </header>

        <div class="tk-page__composer" data-test="tasks-composer">
          <shape-shift-task-input
            v-if="tkShapeShift"
            ref="tkComposer"
            :placeholder="tkShapeShiftPlaceholder"
            :default-due="tkComposerDefaultDue"
            :max-sort-id="maxSortIdForNewTask"
            @saving="onComposerSaving"
            @saved="tkOnSaved"
          />
          <task-composer
            v-else
            ref="tkComposer"
            variant="inline"
            :placeholder="tkComposerPlaceholder"
            :default-due="tkComposerDefaultDue"
            :open-counts="tkComposerOpenCounts"
            :max-sort-id="maxSortIdForNewTask"
            @saving="onComposerSaving"
            @saved="tkOnSaved"
          />
          <div v-if="tkNote" class="tk-page__composer-note" data-test="tasks-composer-note" role="status">{{ tkNote }}</div>
          <div v-if="tkAddDue" class="tk-page__composer-note" data-test="tasks-add-due" role="status">
            The next task you add is due {{ tkAddDueLabel }}.
            <button type="button" class="tk-page__note-link" data-test="tasks-add-due-clear" @click="tkClearAddDue">Use today instead</button>
          </div>
          <button
            v-if="isSuperAdmin"
            type="button"
            class="tk-page__trial"
            :class="{ 'tk-page__trial--back': tkShapeShift }"
            data-test="tasks-shapeshift-toggle"
            :data-target="tkShapeShift ? 'off' : 'on'"
            @click="tkToggleShapeShift"
          >
            <svg v-if="tkShapeShift" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3zM19 16l.7 1.8 1.8.7-1.8.7L19 21l-.7-1.8-1.8-.7 1.8-.7L19 16z" /></svg>
            <span>{{ tkShapeShift ? 'Back to the classic input' : 'Try the new smart input' }}</span>
          </button>
        </div>

        <div v-if="tkBusy" class="tk-page__skeleton" data-test="tasks-skeleton" aria-hidden="true">
          <span v-for="n in 5" :key="n" class="tk-page__skel"></span>
        </div>

        <tasks-calendar
          v-else-if="tkLayout === 'calendar'"
          :tasks="tkCalendarTasks"
          :ctx="tkCtx"
          :employees="employees || []"
          :narrow="tkNarrow"
          :show-done="tkCalShowDone"
          @open="tkOpen"
          @reschedule="tkReschedule"
          @add="tkAddOnDay"
          @update:show-done="tkCalShowDone = $event"
        />

        <tasks-workload
          v-else-if="tkLayout === 'workload'"
          :tasks="tkCalendarOpen"
          :ctx="tkCtx"
          :employees="employees || []"
          :narrow="tkNarrow"
          @open="tkOpen"
        />

        <template v-else-if="tkIsMyDay">
          <overdue-tray
            :tasks="tkDay.overdue"
            :ctx="tkCtx"
            :employees="employees || []"
            :movable-ids="tkMovableIds"
            :moving="tkMoving"
            @open="tkOpen"
            @move="tkMoveOne"
            @move-all="tkMoveAll"
          />
          <tasks-section
            section-key="today"
            title="Today"
            :subtitle="tkTodaySub"
            :rows="tkDay.today"
            :ctx="tkCtx"
            :employees="employees || []"
            hide-self
            :flash-id="tkFlashId"
            empty-text="Nothing left for today."
            @open="tkOpen"
          />
          <tasks-section
            v-if="tkDay.comingUp.length"
            section-key="comingUp"
            title="Coming up"
            subtitle="Next 7 days"
            :rows="tkDay.comingUp"
            :ctx="tkCtx"
            :employees="employees || []"
            :limit="tkComingUpShown"
            :more-link-text="`See all ${tkDay.comingUp.length} in Upcoming`"
            show-due
            hide-self
            :flash-id="tkFlashId"
            @open="tkOpen"
            @link="tkSetView('upcoming')"
          />
          <tasks-section
            v-if="tkDay.doneToday.length"
            section-key="doneToday"
            title="Done today"
            :subtitle="`${tkDay.doneToday.length} finished`"
            :rows="tkDay.doneToday"
            :ctx="tkCtx"
            :employees="employees || []"
            hide-self
            @open="tkOpen"
          />
        </template>

        <template v-else>
          <tasks-section
            v-for="g in tkGroups"
            :key="`${tkRailKey}-${g.key}`"
            collapsible
            :section-key="g.key"
            :title="g.title"
            :hint="g.hint"
            :dot="g.dot"
            :rows="g.rows"
            :ctx="tkCtx"
            :employees="employees || []"
            :limit="g.limit"
            show-due
            :hide-self="tkHideSelf"
            :flash-id="tkFlashId"
            @open="tkOpen"
          />
          <p v-if="!tkGroups.length" class="tk-page__empty" data-test="tasks-empty">{{ tkEmptyText }}</p>
        </template>
      </main>
    </div>
  </div>
  <template v-else>
  <followups-ui-toggle target="new" page-key="tasks" @toggle="fuSetUi" />
  <TasksSkeleton v-if="busy && !tasksLoaded" />
  <!-- *** MODIFIED: Update :show and text logic *** -->
  <b-overlay :show="isDeleting || isDeletingSingle" v-show="!busy || tasksLoaded">
    <template #overlay>
      <!-- START MODIFIED SECTION -->
      <div class="overlay-content">
        <div class="spinner-container">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
        </div>
        </div>
        <p id="cancel-label" class="loading-text">
          <!-- Conditionally display text based on various loading states -->
          {{ isLoadingCompletedTasks ? 'Loading completed tasks...' : isDeleting ? 'Deleting completed tasks...' : isDeletingSingle ? 'Deleting task...' : 'Loading tasks...' }}
        </p>
      </div>
      <!-- END MODIFIED SECTION -->
    </template>
    <!-- *** END MODIFICATION *** -->
    <div class="ToDo">
      <ErrorDisplay/>
      <!-- v-show, not v-if: tasksLoaded flips true before the task arrays fill, so showIntro is
           true for ~0.5 s on every fresh load; v-if destroyed the composer (and any text already
           typed) in that window. Keep it mounted and hidden. -->
      <TaskComposer
        ref="composer"
        v-show="!showIntro"
        :entrance="introDismissed && !hasTasks"
        :max-sort-id="maxSortIdForNewTask"
        :available-tags="computedAvailableTags"
        autofocus
        @saving="onComposerSaving"
        @saved="onComposerSaved"
      >
        <template #actions>
          <div v-if="hasTasks" class="task-report-icon">
            <i id="task-report" style="font-size: 20px" class="la la-bar-chart-o px-2 pull-right" @click="showTaskReports"/>
          </div>
          <button type="button" class="task-settings-btn" @click="openTaskSettings" title="Task Settings">
            <i class="fa fa-cog"></i>
          </button>
        </template>
      </TaskComposer>

      <div class="mt-3">
        <!-- *** MODIFIED: Update v-if condition to show filters when taskID is present *** -->
        <b-row align-h="start" align-v="end" class="mb-2" v-if="hasTasks || taskID">
        <!-- *** END MODIFIED *** -->
          <b-col md="12" lg="12" xl="12">
            <div class="task-controls">
              <!-- *** RESTORED: Show All Tasks Link (conditional) *** -->
              <div v-if="taskID" class="show-all-tasks-link mb-2">
                <a href="#" @click.prevent="showAllTasks" class="text-primary font-weight-bold">
                  <i class="fa fa-list mr-1"></i> Show All Tasks
                </a>
              </div>
              <!-- *** END RESTORED *** -->
              <task-filters
                :patients="patients"
                :employees="activeEmployees"
                :tags="computedAvailableTags"
                @filter-change="handleFilterChange"
                class="task-filters-box"
                ref="taskFilters"
              />
            <!-- Quick Actions Section -->
            <div class="quick-actions-section mt-2 mb-2">
              <!-- Removed Quick Actions Label -->
              <div class="quick-filters">
                <b-button
                  v-b-tooltip.hover title="Quick Action: Show tasks due today"
                  size="sm"
                  :variant="isTodayActive ? 'primary' : 'outline-primary'"
                  class="mr-2 quick-action-button"
                  @click="toggleTodayFilter"
                >
                    <i class="fa fa-calendar-check-o mr-1"></i> Today's Tasks
                </b-button>
                <b-button
                  v-b-tooltip.hover title="Quick Action: Show tasks assigned to me"
                  size="sm"
                  :variant="isAssignedToMeActive ? 'info' : 'outline-info'"
                  class="quick-action-button"
                  @click="toggleAssignedToMeFilter"
                >
                    <i class="fa fa-user mr-1"></i> Assigned To Me
                </b-button>
              </div>
            </div>
            <task-search
              v-if="searchClient"
              :search-client="searchClient"
              :siteID="siteID"
                @search="handleSearch"
                @task-click="opentaskdetails"
                class="task-search-box"
              />
            </div>
          </b-col>
        </b-row>

        <!-- Empty State for intro (new users) or when no tasks exist -->
        <div v-if="(showIntro || (!allTasks || allTasks.length === 0)) && !introDismissed" class="tasks-empty-state-container">
          <div class="tasks-empty-state tasks-empty-state--detailed" v-if="showIntro || (tasksLoaded && !hasTasks)">
            <div class="empty-state-header">
              <div class="empty-state-visual">
                <div class="visual-circles">
                  <div class="circle circle-1"></div>
                  <div class="circle circle-2"></div>
                  <div class="circle circle-3"></div>
                </div>
                <div class="visual-icon">
                  <i class="fa fa-check-square-o"></i>
                </div>
              </div>
              <h3 class="empty-state-title">Streamline Your Workflow with Tasks</h3>
              <p class="empty-state-text">Organize, assign, and track your practice's daily work with powerful task management features.</p>
              <div class="empty-state-actions empty-state-actions--top">
                <button class="empty-state-btn empty-state-btn--primary" @click="focusTaskInput">
                  <i class="fa fa-plus mr-2"></i> Start Creating Tasks
                </button>
              </div>
            </div>

            <div class="empty-state-features-grid">
              <!-- Create Tasks -->
              <div class="feature-card">
                <div class="feature-icon feature-icon--success">
                  <i class="fa fa-check-circle"></i>
                </div>
                <div class="feature-content">
                  <h4 class="feature-title">Create Tasks</h4>
                  <p class="feature-desc">Keep track of your work with simple task creation</p>
                  <div class="feature-example">
                    <span class="example-label">Example:</span>
                    <span class="example-text">Verify insurance for John Smith by Dec 21st</span>
                  </div>
                </div>
              </div>

              <!-- Assign Tasks -->
              <div class="feature-card">
                <div class="feature-icon feature-icon--primary">
                  <i class="fa fa-user-plus"></i>
                </div>
                <div class="feature-content">
                  <h4 class="feature-title">Assign Tasks</h4>
                  <p class="feature-desc">Use <code>@</code> to assign tasks to team members</p>
                  <div class="feature-example">
                    <span class="example-label">Example:</span>
                    <span class="example-text">Order dental supplies tomorrow <strong>@Jane.Smith</strong></span>
                  </div>
                </div>
              </div>

              <!-- Tag Patients -->
              <div class="feature-card">
                <div class="feature-icon feature-icon--info">
                  <i class="fa fa-user-circle"></i>
                </div>
                <div class="feature-content">
                  <h4 class="feature-title">Tag Patients</h4>
                  <p class="feature-desc">Use <code>#</code> to associate tasks with specific patients</p>
                  <div class="feature-example">
                    <span class="example-label">Example:</span>
                    <span class="example-text">Verify insurance for <strong>#Peter</strong> <strong>@Jane.Smith</strong></span>
                  </div>
                </div>
              </div>

              <!-- Get Notified -->
              <div class="feature-card">
                <div class="feature-icon feature-icon--warning">
                  <i class="fa fa-bell"></i>
                </div>
                <div class="feature-content">
                  <h4 class="feature-title">Get Notified</h4>
                  <p class="feature-desc">Receive alerts when tasks are assigned to you or when due dates are approaching</p>
                </div>
              </div>

              <!-- Natural Language Dates -->
              <div class="feature-card">
                <div class="feature-icon feature-icon--info">
                  <i class="fa fa-calendar"></i>
                </div>
                <div class="feature-content">
                  <h4 class="feature-title">Natural Language Dates</h4>
                  <p class="feature-desc">Add due dates using plain language</p>
                  <div class="feature-example">
                    <span class="example-label">Example:</span>
                    <span class="example-text">Call patient by <strong>next Monday</strong></span>
                  </div>
                </div>
              </div>

              <!-- Recurring Tasks -->
              <div class="feature-card">
                <div class="feature-icon feature-icon--primary">
                  <i class="fa fa-refresh"></i>
                </div>
                <div class="feature-content">
                  <h4 class="feature-title">Recurring Tasks</h4>
                  <p class="feature-desc">Create repeating tasks with simple phrases</p>
                  <div class="feature-example">
                    <span class="example-label">Example:</span>
                    <span class="example-text">Meeting with staff <strong>every Monday</strong></span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bottom CTA removed — moved to above-the-fold position in empty-state-header -->
          </div>
          <!-- No results from filters -->
          <div v-else-if="tasksLoaded && hasTasks" class="tasks-empty-state">
            <div class="empty-state-visual">
              <div class="visual-circles">
                <div class="circle circle-1"></div>
                <div class="circle circle-2"></div>
                <div class="circle circle-3"></div>
              </div>
              <div class="visual-icon filter-icon">
                <i class="fa fa-filter"></i>
              </div>
            </div>
            <h3 class="empty-state-title">No Tasks Match Your Filters</h3>
            <p class="empty-state-text">Try adjusting your filter criteria to see more tasks.</p>
            <button class="empty-state-btn" @click="clearAllFiltersAndReset">
              <i class="fa fa-times mr-2"></i> Clear All Filters
            </button>
          </div>
        </div>

        <div v-else-if="allTasks && allTasks.length > 0 && !showIntro" class="tasks-scroll-container">
          <div class="ToDo__items">
            <!-- Column headers -->
            <div class="task-header">
              <div class="header-checkbox"></div>
              <div class="header-description sortable" @click="handleSort('desc')">
                Task <i class="fa fa-sort"></i>
              </div>
              <div class="header-attachment">
                <i class="fa fa-file-pdf header-icon" title="Attachments"></i>
              </div>
              <div class="header-subtasks">
                <i class="fa fa-list-ul header-icon" title="Subtasks"></i>
              </div>
              <div class="header-comments">
                <i class="fa fa-comment-o header-icon" title="Comments"></i>
              </div>
              <div class="header-patients sortable" @click="handleSort('patient')">
                <i class="fa fa-user-circle mr-1"></i> Patient <i class="fa fa-sort"></i>
              </div>
              <!-- Status Header -->
              <div class="header-status sortable" @click="handleSort('status')">
                Status <i class="fa fa-sort"></i>
              </div>
              <!-- Tags Header -->
              <div class="header-tags sortable" @click="handleSort('tags')">
                Tags <i class="fa fa-sort"></i>
              </div>
              <!-- Repeats Header -->
              <div class="header-repeats sortable" @click="handleSort('repeats')">
                Repeats <i class="fa fa-sort"></i>
              </div>
              <div class="header-date sortable" @click="handleSort('date')">
                Due <i class="fa fa-sort"></i>
              </div>
              <div class="header-creator sortable" @click="handleSort('creator')">
                Creator <i class="fa fa-sort"></i>
              </div>
              <div class="header-assignee sortable" @click="handleSort('assignee')">
                Assigned <i class="fa fa-sort"></i>
              </div>
              <div class="header-actions"></div>
            </div>
            <div class="header-spacer"></div>

            <todo-item
              v-for="(item, index) in allTasks"
              :key="item.id"
              :data-task-id="item.id"
              :item="item"
              :moreThanOneItems="allTasks.length > 1"
              :isLoading="allTasksLoadingStatus[index]"
              :selected="item.id === taskID && allTasksSelectedStatus[index]"
              :isNewlyAdded="item.id === newlyAddedTaskId"
              @edit="opentaskdetails($event.id)"
              @change="handleItemChange"
              @delete="deleteItem($event, index, 'alltasks')"
              @deleteThisInstanceOnly="deleteThisInstanceOnly"
              @filter="handleColumnFilter"
              @status-click="handleColumnFilter('status')"
            />
          </div>
        </div>
      </div>

    </div>
  </b-overlay>
  </template>
  <!-- The page's ONE slide-out outlet, outside both branches: TodoDetails (and, from Phase B3, the
       new task drawer) open through it whichever branch is on screen. The legacy branch's outlet
       used to sit inside the b-overlay above (hidden while it loaded); one outlet serves both
       (cypress/node-tests/slideout-outlet-guard.test.js). -->
  <slideout-panel ref="taskdetailspage"></slideout-panel>
  </div>
</template>

<script>
/* eslint-disable */
import Vue from 'vue';
import Draggable from 'vuedraggable'
import TodoItem from './components/TodoItem'
import { ToDoService } from '@/services/ToDoService'
import moment from 'moment-timezone';
//import At from 'vue-at';
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import mixin from '../../mixin.js';
import _ from 'lodash';
import TodoDetails from './components/TodoDetails';
import TaskReports from './components/TaskReports';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import algoliasearch from 'algoliasearch/lite';
import 'instantsearch.css/themes/algolia-min.css';
import NonEscapedHighlight from './components/NonEscapedHighlight'
import TaskFilters from './components/TaskFilters'
import TaskSearch from './components/TaskSearch'
// Import the default export from chrono-simple
import simpleParser from '@/utils/chrono-simple';

import tasksImg from '../../assets/tasks.png';
import TasksSkeleton from './TasksSkeleton.vue';
import TaskSettings from './components/TaskSettings.vue';
import TaskComposer from '@/components/TaskComposer/TaskComposer.vue';
import ShapeShiftTaskInput from '@/components/ShapeShift/ShapeShiftTaskInput.vue';
import { shouldUseShapeShiftInput, setShapeShiftInput } from '@/utils/shapeshiftGate.js';
// Tasks page redesign (design "Tasks 2a", Phase B1) — the new branch only. See fuUseNewUi.
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import TasksSidebar from './components/TasksSidebar.vue';
import TasksSection from './components/TasksSection.vue';
import OverdueTray from './components/OverdueTray.vue';
import TaskDrawer from './components/TaskDrawer.vue';
import TaskReportsPage from './components/TaskReportsPage.vue';
import TaskSettingsPanel from './components/TaskSettingsPanel.vue';
import { normalizeTask } from '@/utils/tasks/taskReport.js';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';
import { cleanTaskContent } from '@/utils/tasks/completeTask.js';
import {
  VIEWS, COMING_UP_SHOWN, TEAM_SHOWN, TAGS_SHOWN, tasksForView, viewCounts, myDayScope, myDaySections, sections,
  groupList, progress, teamCounts, tagCounts, filterBy, moveAllToTodayPatch, combineTasks, assigneeIDs, isSiteWideRole,
  loadPlan, addDays, dueDayOf, dueAtStrFormat, rescheduleToDayPatch,
} from '@/utils/tasks/myDay.js';
import { addedNote, openTaskCounts } from '@/utils/tasks/composerChips.js';
import TasksCalendar from './components/TasksCalendar.vue';
import TasksWorkload from './components/TasksWorkload.vue';
import { tasksLayout, setTasksLayout, layoutView, routeIntent } from '@/utils/tasksLayoutGate.js';

// The redesign's list-view groups (myDay.js groupList keys), in the design's order.
const TK_GROUPS = [
  { key: 'overdue', title: 'Overdue', dot: 'late', hint: 'Oldest first', limit: 6 },
  { key: 'today', title: 'Today', dot: 'today', hint: '', limit: 6 },
  { key: 'thisWeek', title: 'This week', dot: 'muted', hint: '', limit: 6 },
  { key: 'later', title: 'Later', dot: 'muted', hint: '', limit: 6 },
  { key: 'noDate', title: 'No due date', dot: 'muted', hint: '', limit: 6 },
  { key: 'done', title: 'Completed today', dot: 'done', hint: 'Stays here until tomorrow', limit: 4 },
];
const TK_DEFAULT_TIME_ZONE = 'America/New_York';
const TK_RING_C = 2 * Math.PI * 27;
// The new task drawer's width (TaskDrawer.vue header); the classic TodoDetails panel stays 750.
const TK_DRAWER_WIDTH = 540;
// The redesign's tablet breakpoint ($fu-tablet-max in src/pages/Followups/_tokens.scss).
const TK_NARROW_QUERY = '(max-width: 899.98px)';
// The Task settings panel (design 2a) — the classic TaskSettings panel stays 720.
const TK_SETTINGS_WIDTH = 440;

/**
 * Task reports (?screen=reports) — module state, because Layout.vue keys <router-view> on
 * $route.fullPath: every ?screen change MOUNTS A NEW ToDo. So:
 *   • tkReportsListener — the tasks/getTaskReports Firestore listener this page started
 *     ({ siteID, handle, delivered }). The action adds a NEW listener on every call and nothing
 *     unsubscribes it before logout (customer/clearFirestoreListeners), so a fresh ToDo re-uses a
 *     live one instead of stacking another per Reports visit.
 *   • tkReportsHistoryKey — vue-router's history.state.key of the entry Reports pushed, so "← Tasks"
 *     goes BACK (browser Back then returns to the list too) only when that entry is the current one;
 *     a deep link to ?screen=reports replaces instead.
 */
let tkReportsListener = null;
let tkReportsHistoryKey = null;
const tkIsNavDup = err => !!err && (err.name === 'NavigationDuplicated' || /redundant navigation|NavigationDuplicated/i.test(String(err.message || '')));

Array.prototype.repeat= function(what, L){
  while(L) this[--L]= what;
  return this;
}

export default {
  name: 'ToDo',
  components: { TodoItem, Draggable, ErrorDisplay, NonEscapedHighlight, TaskFilters, TaskSearch, TasksSkeleton, TaskComposer, ShapeShiftTaskInput, FollowupsUiToggle, TasksSidebar, TasksSection, OverdueTray, TaskReportsPage, TasksCalendar, TasksWorkload },
  props: ['taskID'],
  mixins: [mixin],

  async mounted() { // Make mounted async
    // Restore introDismissed from localStorage so intro never reappears once dismissed
    if (this.siteID && localStorage.getItem(`teamio-tasks-intro-dismissed-${this.siteID}`) === 'true') {
      this.introDismissed = true;
    }

    // *** REVISED: Fetch tasks and open panel if taskID is provided ***
    if (this.taskID) {
      // First fetch all tasks so the list is populated
      await this.fetchToDos();
      this.tkFetched = true;
      // Then open the task details panel for the specific task (the redesigned page opens it
      // through tkOpen instead — tkMaybeOpenDeepLink)
      if (this.fuUseNewUi) {
        this.tkMaybeOpenDeepLink();
      } else {
        this.$nextTick(() => {
          this.opentaskdetails(this.taskID);
        });
      }
    } else {
      // console.log('Mounting for all tasks list.');
      this.fetchToDos(); // Fetch all tasks (handles its own busy state)
    }
    // *** END REVISED ***

    // Add a safety timeout to force loading/deleting to end after 3 seconds regardless of state
    // (Consider adjusting timeout duration or logic if needed)
    setTimeout(() => {
      if (this.busy) {
        // console.log('Safety timeout triggered - forcing busy to false');
        this.busy = false;
      }
    }, 3000);
    
    // Subscribe to site tags
    if (this.siteID) {
      try {
        await this.$store.dispatch('tasks/subscribeSiteTaskTags', {
          siteID: this.siteID
        });
      } catch (error) {
        console.error('ToDo: Error subscribing to task-tags:', error);
      }
    }

    this.initSearchClient();
  },

  created() {
    this.tkApplyRouteIntent();
    if(this.allSettings)
      this.init();
  },

  data () {
    return {
      busy: false,
      // isLoadingCompletedTasks: false, // Removed duplicate, already added below
      isTodayActive: false, // NEW: Track quick action state
      isAssignedToMeActive: false, // NEW: Track quick action state
      searchClient: null, // set in mounted() via fetchAlgoliaSearchKey (server-minted, site-scoped)
      // siteTags: [], // Removed - Wiki tags are not used here
      search: '',
      employeeFilter: '',
      // Add this to enable debug info for date parsing
      showDebugInfo: true,
      assignedToMeTaskLoadingStatus: [].repeat(false, 50),
      assignedToMeTaskSelectedStatus: [].repeat(true, 50),
      myTaskLoadingStatus: [].repeat(false, 50),
      myTaskSelectedStatus: [].repeat(true, 50),
      myEmployeesTaskLoadingStatus: [].repeat(false, 50),
      myEmployeesTaskSelectedStatus: [].repeat(true, 50),
      allMyTasksLoadingStatus: [].repeat(false, 50),
      allTasksLoadingStatus: [].repeat(false, 50),
      allTasksSelectedStatus: [].repeat(true, 50),
      //assignedToMeTasksView: 'Show all tasks',
      //createdByMeTasksView: 'Show all tasks',
      createdByMeTaskOptions: ['Show all tasks', 'Show incomplete tasks only', 'Show overdue tasks only', 'Delete all completed tasks'],
      assignedToMeTaskOptions: ['Show all tasks', 'Show incomplete tasks only', 'Show overdue tasks only'],
      tasksByTeamMembersOptions: ['Show all tasks', 'Show incomplete tasks only', 'Show overdue tasks only'],
      taskSortOption: 'default', // Keep 'default' for initial status sort
      // Updated options to reflect column sorting - Note: these values are used internally for logic, not display
      taskSortOptions: [
        { text: 'Default (Status)', value: 'default' },
        { text: 'Description (A-Z)', value: 'desc-asc' },
        { text: 'Description (Z-A)', value: 'desc-desc' },
        { text: 'Patient (A-Z)', value: 'patient-asc' },
        { text: 'Patient (Z-A)', value: 'patient-desc' },
        { text: 'Tags (A-Z)', value: 'tags-asc' }, // Note: Sorting by multiple tags might be complex
        { text: 'Tags (Z-A)', value: 'tags-desc' },
        { text: 'Status (Completed First)', value: 'status-desc' }, // Explicit status sort
        { text: 'Status (Incomplete First)', value: 'status-asc' }, // Explicit status sort
        { text: 'Due Date (Newest)', value: 'date-desc' },
        { text: 'Due Date (Oldest)', value: 'date-asc' }
      ],
      tasksImg,
      wikiPages: [],
      editInProgress: false,
      workInProgress: false,
      activeFilters: {
        // status: 'Show all tasks', // REMOVED
        patients: [],
        assignees: [],
        creators: [], // Add creators array
        tags: [],
        excludeTags: [], // NEW: Tags to exclude from results
        // repeatStatus: 'Show all', // REMOVED
        // NEW flags for combined filter
        showIncomplete: false,
        showOverdue: false,
        showNewComments: false,
        showRepeating: false,
        showNonRepeating: false,
        showCompletedOnly: false, // NEW: Flag for completed tasks filter
        dueToday: false, // NEW: Filter flag for today's tasks
      },
      allTasksLoadingStatus: [].repeat(false, 50),
      allTasksSelectedStatus: [].repeat(true, 50),
      detectedDateInfo: {
        hasDate: false,
        displayText: '',
        date: null,
        isRecurring: false,
        frequency: null,
        daysOfWeek: null,
        dayOfMonth: null, // Add dayOfMonth to reset state
        repeatOnMonth: null, // Reset repeatOnMonth
        isLastDayOfMonth: false // Add isLastDayOfMonth to reset state
      },
      introDismissed: false, // Track if user has clicked the 'Start' button (initialized from localStorage in mounted)
      isDeleting: false, // Add this new state
      isLoadingCompletedTasks: false, // Add state for loading completed tasks specifically
      isDeletingSingle: false, // Add state for deleting a single task
      newlyAddedTaskId: null, // Track newly added task for scroll+highlight
      // ---- Tasks page redesign (new branch only) ----
      fuUiVersion: 0,            // bumped by the Classic / New view links so fuUseNewUi re-reads storage
      ssVersion: 0,              // bumped by the "smart input" link so tkShapeShift re-reads storage
      tkLayoutVersion: 0,        // bumped by the List / Calendar / Workload switch so tkLayout re-reads storage
      tkCalShowDone: false,      // Calendar: also draw completed tasks (struck through)
      tkAddDue: null,            // 'YYYY-MM-DD' set by the calendar's "+" on a day: the composer's default due day
      tkComposeOnLoad: false,    // ?compose=1 (Ctrl+K "New task"): put the caret in the composer once it is on screen
      tkView: 'myDay',           // myDay.js VIEWS key
      tkEmp: null,               // a picked teammate (String EmployeeID) — replaces the view
      tkTag: null,               // a picked tag — replaces the view
      tkQ: '',                   // the sidebar search box
      tkClock: Date.now(),       // "today" — re-read every minute while the new branch shows
      tkClockTimer: null,
      tkNarrow: false,           // below the tablet breakpoint: the sidebar becomes a filter row
      tkMedia: null,
      tkCompletedRequested: false,
      tkFetched: false,          // mounted's fetchToDos has returned
      tkDeepLinkedID: null,      // the taskID prop already opened
      tkMoving: false,
      tkFlashId: null,           // a task just added from the composer
      tkFlashTimer: null,
      tkNote: '',                // "Added for Fri, Oct 9. You’ll find it under Upcoming." (6 s)
      tkNoteTimer: null,
      tkScreen: this.$route && this.$route.query && this.$route.query.screen === 'reports' ? 'reports' : 'list',
      tkReportsRequested: false, // this ToDo asked for the site's tasks (tasks/getTaskReports) — once
      tkReportsWaiting: false,   // …and the first allTasksForSite snapshot since has not arrived yet
    }
  },

  beforeDestroy() {
    // Cleanup task-tags subscription
    if (this.siteID) {
      this.$store.dispatch('tasks/unsubscribeSiteTaskTags');
    }
    this.tkStop();
    if (this.tkFlashTimer) clearTimeout(this.tkFlashTimer);
    if (this.tkNoteTimer) clearTimeout(this.tkNoteTimer);
  },

  methods: {
    // ---- Tasks page redesign (design "Tasks 2a", Phase B1 — new branch only) --------------------

    /** The "Classic view" / "New view" links: a Super Admin re-renders in place, an office user reloads (uiSwitch.js). */
    fuSetUi(target) {
      if (switchUi('tasks', target, { isSuperAdmin: this.isSuperAdmin, userKey: this.userID }) === 'rerender') this.fuUiVersion += 1;
    },

    /**
     * Opens a task from the new page in the new task drawer (TaskDrawer, 540 wide). Every open on
     * the new page — row click, deep link — comes through here. It gathers what opentaskdetails
     * gathers for TodoDetails (the wiki pages, fetched once if not loaded yet; the patient roster;
     * the site's tags) but hands over the list's copy of the task: the drawer reads the full doc +
     * comments itself (tasks/getTaskDetails on mount), so fetching it here as well would read it
     * twice. The classic page keeps opentaskdetails → TodoDetails, unchanged.
     *
     * After a save the drawer emits the task: updateTaskInLists patches the store lists at once,
     * exactly as opentaskdetails' TodoDetails listener does (the Firestore listener then replaces
     * the lists with the same data). After a delete it emits { id }: tkRemoveFromLists drops it
     * at once (a no-op if the listener got there first).
     */
    async tkOpen(task) {
      if (!task || !task.id) return;
      if (!this.wikiPages || this.wikiPages.length === 0) {
        try {
          const results = await this.getWikiPages({ siteID: this.siteID });
          if (results && results.length > 0) this.wikiPages = results[0] || [];
        } catch (error) {
          console.error('[Tasks] wiki pages could not be loaded before opening the task:', error);
          this.wikiPages = [];
        }
      }
      const listed = this.tkFindListed(task.id);
      this.$showPanel({
        component: TaskDrawer,
        openOn: 'right',
        width: TK_DRAWER_WIDTH,
        props: {
          task: Object.assign({}, listed || task),
          wikiPages: this.wikiPages,
          patients: this.patients,
          siteTags: this.computedAvailableTags,
          // Callbacks as PROPS: vue2-slideout-panel ignores `listeners` (it binds only closePanel),
          // so the list hears about drawer saves/deletes this way, before the Firestore listener.
          onTaskUpdated: (updatedTask) => {
            if (updatedTask && updatedTask.id) this.updateTaskInLists(updatedTask);
          },
          onTaskDeleted: (payload) => {
            if (payload && payload.id) this.tkRemoveFromLists(payload.id);
          },
        },
      });
    },

    /** The store lists' copy of a task (the open lists, then the site's completed ones), or null. */
    tkFindListed(id) {
      const lists = [this.myCreatedTasks, this.assignedToMeTasks, this.myEmployeesTasks, this.completedTasksForSite];
      for (let i = 0; i < lists.length; i += 1) {
        const hit = (lists[i] || []).find(t => t && String(t.id) === String(id));
        if (hit) return hit;
      }
      return null;
    },

    /** A task deleted from the drawer leaves the lists now, not when the Firestore listener fires. */
    tkRemoveFromLists(id) {
      [this.myCreatedTasks, this.assignedToMeTasks, this.myEmployeesTasks].forEach((list) => {
        if (!Array.isArray(list)) return;
        const i = list.findIndex(t => t && String(t.id) === String(id));
        if (i !== -1) list.splice(i, 1);
      });
    },

    tkSetView(key) {
      // Under Calendar / Workload the office-wide views all mean "everyone" (layoutView).
      this.tkView = layoutView(this.tkLayout, key);
      this.tkEmp = null;
      this.tkTag = null;
    },

    /**
     * What the Ctrl+K palette's commands ask for: /app/todo?layout=calendar · ?view=assignedToMe · ?compose=1
     * (src/utils/tasksLayoutGate.js routeIntent — unknown values are ignored, a layout only for a Super Admin).
     * Layout.vue keys <router-view> on fullPath, so every such navigation re-mounts the page and lands here.
     */
    tkApplyRouteIntent() {
      const intent = routeIntent(this.$route && this.$route.query, { isSuperAdmin: this.isSuperAdmin });
      if (intent.layout) setTasksLayout(browserStorage(), intent.layout);
      if (intent.view) this.tkView = intent.view;
      if (intent.compose) this.tkComposeOnLoad = true;
    },

    /** Caret into whichever composer is on screen (the classic one has focus(); the smart input is found in the DOM). */
    tkFocusComposer() {
      const c = this.$refs.tkComposer;
      if (c && typeof c.focus === 'function') { c.focus(); return true; }
      const box = this.$el && this.$el.querySelector ? this.$el.querySelector('[data-test="tasks-composer"]') : null;
      const el = box && box.querySelector('[contenteditable="true"], textarea, input');
      if (el) { el.focus(); return true; }
      return false;
    },

    /** The calendar's "+" on a day: the next task from the composer is due that day. */
    tkAddOnDay(ymd) {
      this.tkAddDue = ymd;
      this.$nextTick(() => {
        const box = this.$el && this.$el.querySelector ? this.$el.querySelector('[data-test="tasks-composer"]') : null;
        if (box && typeof box.scrollIntoView === 'function') box.scrollIntoView({ block: 'center', behavior: 'smooth' });
        this.tkFocusComposer();
      });
    },

    tkClearAddDue() {
      this.tkAddDue = null;
    },

    tkSetEmp(key) {
      this.tkEmp = key ? String(key) : null;
      if (key) this.tkTag = null;
    },

    tkSetTag(key) {
      this.tkTag = key ? String(key) : null;
      if (key) this.tkEmp = null;
    },

    /** "Done today" / "Completed today" need the site's completed tasks — the new branch only, once. */
    tkLoadCompleted() {
      if (this.tkCompletedRequested || !this.fuUseNewUi || !this.siteID) return;
      this.tkCompletedRequested = true;
      const plan = loadPlan(this.user && this.user.Role);
      Promise.resolve(this.$store.dispatch(plan.completed, { siteID: this.siteID })).catch((err) => {
        console.error('[Tasks] loading completed tasks failed:', err);
        this.tkCompletedRequested = false;
      });
    },

    /** /app/todo/:taskID on the new page: open that task once, after the tasks have loaded. */
    tkMaybeOpenDeepLink() {
      if (!this.fuUseNewUi || !this.taskID || !this._isMounted) return;
      if (!this.tasksLoaded && !this.tkFetched) return;
      if (String(this.tkDeepLinkedID) === String(this.taskID)) return;
      this.tkDeepLinkedID = this.taskID;
      this.tkOpen({ id: this.taskID });
    },

    tkToggleShapeShift() {
      setShapeShiftInput(browserStorage(), !this.tkShapeShift);
      this.ssVersion += 1;
    },

    /** Super Admin preview: List / Calendar / Workload (src/utils/tasksLayoutGate.js). */
    tkSetLayout(layout) {
      setTasksLayout(browserStorage(), layout);
      this.tkLayoutVersion += 1;
    },

    /**
     * The calendar's drop: one tasks/updateTask with the due date moved to the dropped day (the same
     * save "Move to today" uses — myDay.js rescheduleToDayPatch skips repeating / completed tasks).
     */
    async tkReschedule({ task, ymd }) {
      const patch = rescheduleToDayPatch(task, ymd, this.tkCtxNow());
      if (!patch) return;
      const moved = await this.tkDispatchMoves([patch]);
      if (moved && this.$toast && typeof this.$toast.success === 'function') {
        this.$toast.success(`Moved to ${moment(ymd, 'YYYY-MM-DD').format('ddd, MMM D')}.`);
      }
    },

    tkOnSaved(taskId, task) {
      if (!taskId) return;
      this.tkAddDue = null; // the calendar's "+" day was for THIS task
      this.tkFlashId = taskId;
      if (this.tkFlashTimer) clearTimeout(this.tkFlashTimer);
      this.tkFlashTimer = setTimeout(() => {
        this.tkFlashId = null;
        this.tkFlashTimer = null;
      }, 4000);
      this.tkShowAddedNote(taskId, task);
    },

    /**
     * The design's "Added for Fri, Oct 9. You’ll find it under Upcoming." — only when the view on screen
     * does not list the task just added. Visibility uses the SAME myDay.js rules the lists use, on the
     * page's own input with the saved task (and its id) put first.
     */
    tkShowAddedNote(taskId, task) {
      if (this.tkNoteTimer) clearTimeout(this.tkNoteTimer);
      this.tkNoteTimer = null;
      this.tkNote = '';
      if (!task || typeof task !== 'object') return;
      const id = typeof taskId === 'object' ? taskId.id : taskId;
      const saved = Object.assign({}, task, { id: id == null ? task.id : id });
      const ctx = this.tkCtx;
      const base = this.tkInput;
      const input = Object.assign({}, base, { myCreated: [saved].concat(base.myCreated || []) });
      const listed = t => t && (t === saved || (saved.id != null && t.id === saved.id));
      const visible = this.tkEmp
        ? (saved.assignees || []).some(a => String(a) === String(this.tkEmp))
        : this.tkTag
          ? (saved.tags || []).indexOf(this.tkTag) !== -1
          : tasksForView(this.tkView, input, ctx).some(listed);
      const employees = this.employees || [];
      const me = String(this.userID);
      const note = addedNote({
        visibleInView: visible,
        dueYmd: dueDayOf(saved, ctx),
        todayYmd: ctx.today,
        assigneeNames: (saved.assignees || []).map((a) => {
          const emp = employees.find(e => e && String(e.EmployeeID) === String(a));
          return { name: emp ? emp.Name : '', isMe: String(a) === me };
        }),
        viewKey: this.tkEmp || this.tkTag ? null : this.tkView,
      });
      if (!note) return;
      this.tkNote = note;
      this.tkNoteTimer = setTimeout(() => {
        this.tkNote = '';
        this.tkNoteTimer = null;
      }, 6000);
    },

    tkCtxNow() {
      return Object.assign({}, this.tkCtx, { now: new Date() });
    },

    /** One tasks/updateTask per patch (never updateMultipleTasks — it writes sortID only). */
    async tkDispatchMoves(patches) {
      if (!patches.length || this.tkMoving) return 0;
      this.tkMoving = true;
      let failed = 0;
      try {
        for (let i = 0; i < patches.length; i += 1) {
          try {
            await this.$store.dispatch('tasks/updateTask', patches[i]);
          } catch (err) {
            failed += 1;
            console.error('[Tasks] moving a task to today failed:', err);
          }
        }
      } finally {
        this.tkMoving = false;
      }
      if (failed && this.$toast) {
        this.$toast.error(`${failed} of ${patches.length} could not be moved. Please try again in a moment.`);
      }
      return patches.length - failed;
    },

    /** The tray's confirmed "Move all to today": every non-repeating overdue task shown. */
    async tkMoveAll() {
      const moved = await this.tkDispatchMoves(moveAllToTodayPatch(this.tkDay.overdue, this.tkCtxNow()));
      if (moved && this.$toast && typeof this.$toast.success === 'function') {
        this.$toast.success(`Moved ${moved} ${moved === 1 ? 'task' : 'tasks'} to today.`);
      }
    },

    /** The Review list's per-row "Move to today" (non-repeating only — the patch helper skips the rest). */
    tkMoveOne(task) {
      return this.tkDispatchMoves(moveAllToTodayPatch([task], this.tkCtxNow()));
    },

    tkMeasure() {
      if (this.tkMedia) this.tkNarrow = !!this.tkMedia.matches;
      else if (typeof window !== 'undefined') this.tkNarrow = window.innerWidth < 900;
    },

    tkStart() {
      this.tkStop();
      this.tkClock = Date.now();
      this.tkClockTimer = setInterval(() => { this.tkClock = Date.now(); }, 60000);
      if (typeof window !== 'undefined') {
        this.tkMedia = typeof window.matchMedia === 'function' ? window.matchMedia(TK_NARROW_QUERY) : null;
        window.addEventListener('resize', this.tkMeasure);
      }
      this.tkMeasure();
    },

    tkStop() {
      if (this.tkClockTimer) clearInterval(this.tkClockTimer);
      this.tkClockTimer = null;
      if (typeof window !== 'undefined') window.removeEventListener('resize', this.tkMeasure);
      this.tkMedia = null;
    },

    // ---- Task reports (design 1c) + Task settings (design 2a) ----

    /**
     * Reports → ?screen=reports (a history entry, so browser Back returns to the list). The route
     * change re-mounts the page (Layout.vue keys <router-view> on fullPath) and the new ToDo opens
     * on Reports; where no remount happens (unit tests) the $route watcher switches the screen.
     * From /app/todo/<id> it goes to /app/todo?screen=reports, so the deep-linked task is not
     * reopened over the report.
     */
    tkOpenReports() {
      if (this.tkScreen === 'reports') return;
      const router = this.$router;
      if (!router || typeof router.push !== 'function') {
        this.tkScreen = 'reports';
        this.tkEnsureReports();
        return;
      }
      const query = Object.assign({}, (this.$route && this.$route.query) || {}, { screen: 'reports' });
      Promise.resolve(router.push({ name: 'ToDo', query }))
        .then(() => {
          const st = typeof window !== 'undefined' && window.history ? window.history.state : null;
          tkReportsHistoryKey = st && st.key ? st.key : null;
        })
        .catch((err) => {
          if (!tkIsNavDup(err)) console.error('[Tasks] opening Task reports failed:', err);
        });
    },

    /** "← Tasks": back to the entry Reports was opened from, else (a deep link) replace the query. */
    tkCloseReports() {
      const router = this.$router;
      if (!router) {
        this.tkScreen = 'list';
        return;
      }
      const st = typeof window !== 'undefined' && window.history ? window.history.state : null;
      if (tkReportsHistoryKey && st && st.key === tkReportsHistoryKey && typeof router.back === 'function') {
        tkReportsHistoryKey = null;
        router.back();
        return;
      }
      const route = this.$route || {};
      const query = Object.assign({}, route.query || {});
      delete query.screen;
      Promise.resolve(router.replace({ name: route.name || 'ToDo', params: route.params || {}, query }))
        .catch((err) => {
          if (!tkIsNavDup(err)) console.error('[Tasks] leaving Task reports failed:', err);
        });
    },

    /**
     * The report reads EVERY task of the site: tasks/getTaskReports (an onSnapshot into
     * state.tasks.allTasksForSite). Dispatched at most once per ToDo, only when Reports is first
     * shown on the new page — and not at all while a listener this page started for the same site
     * is still registered (see tkReportsListener). "Loading" until the first allTasksForSite change.
     */
    tkEnsureReports() {
      if (!this.fuUseNewUi || this.tkScreen !== 'reports' || !this.siteID || this.tkReportsRequested) return;
      this.tkReportsRequested = true;
      const listeners = () => (((this.$store.state || {}).customer || {}).firestoreListeners) || [];
      const live = tkReportsListener && tkReportsListener.siteID === String(this.siteID)
        && listeners().indexOf(tkReportsListener.handle) !== -1;
      const allSite = () => (this.$store.state.tasks || {}).allTasksForSite;
      if (live) {
        // Delivered = a snapshot replaced the list since the dispatch (even while no ToDo watched).
        this.tkReportsWaiting = !tkReportsListener.delivered && allSite() === tkReportsListener.seed;
        return;
      }
      tkReportsListener = null;
      const seed = allSite();
      const before = listeners().slice();
      this.tkReportsWaiting = true;
      let pending;
      try {
        pending = this.$store.dispatch('tasks/getTaskReports', { siteID: this.siteID });
      } catch (err) {
        pending = Promise.reject(err);
      }
      // The action registers its listener synchronously (customer/addFirestoreListener).
      const added = listeners().filter(l => before.indexOf(l) === -1);
      if (added.length === 1) tkReportsListener = { siteID: String(this.siteID), handle: added[0], delivered: false, seed };
      Promise.resolve(pending).catch((err) => {
        console.error('[Tasks] loading the task report failed:', err);
        this.tkReportsWaiting = false;
        this.tkReportsRequested = false;
        if (this.$toast && typeof this.$toast.error === 'function') this.$toast.error('The task report could not be loaded. Please try again.');
      });
    },

    /** The gear → the Task settings panel (design 2a), 440 wide; the backdrop / Esc close it unsaved. */
    tkOpenSettings() {
      this.$showPanel({
        component: TaskSettingsPanel,
        openOn: 'right',
        width: TK_SETTINGS_WIDTH,
        cssClass: 'tsp-slideout',
        props: {},
      });
    },

    async initSearchClient() {
      try {
        const keyData = await this.$store.dispatch('tasks/fetchAlgoliaSearchKey');
        if (keyData && keyData.key) {
          this.searchClient = algoliasearch(
            keyData.appID || process.env.VUE_APP_ALGOLIA_APP_ID,
            keyData.key
          );
        }
      } catch (e) {
        console.warn('[Tasks] Algolia search key unavailable, search disabled', e);
      }
    },

    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage', 'setLoading', 'setLoaded']),
    ...mapActions('wiki', ['getWikiPages', 'getSiteTags']),
    ...mapActions('tasks', ['getTasksForEmployee', 'getTasksForSite', 'getTaskDetails', 'addPersonalTask', 'updateTask', 'updateMultipleTasks', 'deleteTask', 'deleteThisInstanceOfRepeatedTask', 'deleteAllCompletedTasks', 'getCompletedTasksForSite']), // Added getCompletedTasksForSite
    ...mapMutations('tasks', ['setMyEmployeesTasks', 'setEmployeeTasksView', 'setAssignedToMeTasksView', 'setMyTasksIncludesCreatedByMe']), // MODIFIED: Removed setCreatedByMeTasksView
    // Moved mapGetters to computed block below

    // *** NEW: Method to navigate back to all tasks ***
    showAllTasks() {
      this.$router.push({ name: 'ToDo' }); // Assuming 'ToDo' is the route name
    },
    // *** END NEW ***


    dismissIntroAndFocusInput() {
      this.persistIntroDismissed();
      // Use nextTick to ensure the input is visible before trying to focus
      this.$nextTick(() => {
        this.setCaretAtBeginning();
      });
    },

    focusTaskInput() {
      // Focus the task input field (from empty state or intro)
      this.persistIntroDismissed();
      this.$nextTick(() => {
        this.setCaretAtBeginning();
      });
    },

    persistIntroDismissed() {
      this.introDismissed = true;
      if (this.siteID) {
        localStorage.setItem(`teamio-tasks-intro-dismissed-${this.siteID}`, 'true');
      }
    },

    async init() {
        try {
        let results = await this.getWikiPages({siteID: this.siteID});
            //// console.log('Wiki pages API response:', results);

            // Check if results is an array and has data
            if (results && Array.isArray(results)) {
                if (results.length > 0) {
                    if (Array.isArray(results[0])) {
                        // Store the wiki pages in the component
                        this.wikiPages = results[0] || [];
                    } else {
                        // If results[0] is not an array, use the results directly
                        this.wikiPages = results;
                    }
                } else {
                    this.wikiPages = [];
                }

                // Log the extracted wiki pages for debugging
                //// console.log('Extracted wiki pages:', this.wikiPages);
                //// console.log('Wiki pages type:', Array.isArray(this.wikiPages) ? 'Array' : typeof this.wikiPages);
                //// console.log('Wiki pages count:', this.wikiPages.length);
            } else {
                //// console.warn('No wiki pages found in API response, or response is not an array');
                this.wikiPages = [];
            }

        // // Fetch site tags - REMOVED
        // this.siteTags = await this.getSiteTags({siteID: this.siteID});
        } catch (error) {
            console.error('Error initializing wiki pages:', error);
            this.wikiPages = [];
        }
    },

    openTaskSettings() {
      const handle = this.$showPanel({
        component: TaskSettings,
        openOn: 'right',
        width: 720,
        disableBgClick: true,
        disableEscClick: true,
      });
      handle.promise.then(() => { /* panel closed */ });
    },

    showTaskReports(detailType, detailData) {
      var _this = this;
      try {
        const showDetailsHandle = this.$showPanel({
          component : TaskReports,
          openOn: 'right',
          width: 1350,
          disableBgClick: true,
          disableEscClick: true,
          props: {
            detailType,
            detailData
          }
        });

        showDetailsHandle.promise.then(async(result) => {
          //console.log(`Got result: ${JSON.stringify(result, null, 4)}`);
        });

      } catch(err) {
        console.log(err);
      }
    },

    setCaretAtBeginning() {
      // The composer (src/components/TaskComposer/TaskComposer.vue) owns the editor and its
      // caret + highlight — this delegation is the only remaining coupling to it.
      this.$nextTick(() => {
        const c = this.$refs.composer;
        if (c && typeof c.focus === 'function') c.focus();
      });
    },

    getLinkedWikiPageID(content) {
        let regex = /data-teamio-wikipageid="([a-zA-z0-9\-]+).*/;
        let result = regex.exec(content);
        if(result) {
            //console.log(`regex result: ${result}`);
            return result[1];
        } else
            return null;
    },

    // Keep the resetDateInfo method
    resetDateInfo() {
      this.detectedDateInfo = {
        hasDate: false,
        displayText: '',
        date: null,
        isRecurring: false,
        frequency: null,
        daysOfWeek: null,
        dayOfMonth: null,
        repeatOnMonth: null, // Reset repeatOnMonth
        isLastDayOfMonth: false // Ensure reset state matches property name
      };
      // NOTE: selectedDueDate is NOT reset here — it's the user's explicit pick
      // Reset it only after save.
    },

    // Keep getDayOfWeekFromMatch (though it might become redundant if not used elsewhere)
    // Helper method to extract day of week from a match (0=Sun, 1=Mon...)
    getDayOfWeekFromMatch(text) {
      const days = {
        'monday': 1, 'tuesday': 2, 'wednesday': 3, 'thursday': 4,
        'friday': 5, 'saturday': 6, 'sunday': 0
      };
      const dayRegex = /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i;
      const match = text.match(dayRegex);
      if (match) {
        const day = match[1].toLowerCase();
        return [days[day]]; // Return as array for consistency
      }
      return [];
    },

    // Detect recurring patterns in the content
    detectRecurringPattern(content) {
      const result = {
        isRecurring: false,
        pattern: null,
        startDate: null,
        displayText: '',
        rawText: ''
      };

      // Common recurring patterns
      const recurringPatterns = [
        {
          regex: /\b(every|each)\s+(day|daily)\b/i,
          parse: () => ({
            frequency: 'Daily',
            startDate: moment().toDate(),
            displayText: 'Every day starting today'
          })
        },
        {
          regex: /\b(every|each)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i,
          parse: (matches) => {
            const dayName = matches[2].toLowerCase();
            const dayMapping = {
              monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
              thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
            };
            const repeatOn = dayMapping[dayName];

            // Find the next occurrence of this day
            const today = moment().format('ddd');
            let startDate;

            if (today === repeatOn) {
              startDate = moment();
            } else {
              const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              const todayIdx = daysOfWeek.indexOf(today);
              const targetIdx = daysOfWeek.indexOf(repeatOn);
              const daysToAdd = (targetIdx - todayIdx + 7) % 7;
              startDate = moment().add(daysToAdd, 'days');
            }

            return {
              frequency: 'Weekly',
              repeatOn: repeatOn,
              startDate: startDate.toDate(),
              displayText: `Every ${dayName} starting ${startDate.format('MMM D')}`
            };
          }
        },
        {
          regex: /\b(every|each)\s+(other)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i,
          parse: (matches) => {
            const dayName = matches[3].toLowerCase();
            const dayMapping = {
              monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
              thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
            };
            const repeatOn = dayMapping[dayName];

            // Find the next occurrence of this day
            const today = moment().format('ddd');
            let startDate;

            if (today === repeatOn) {
              startDate = moment();
            } else {
              const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              const todayIdx = daysOfWeek.indexOf(today);
              const targetIdx = daysOfWeek.indexOf(repeatOn);
              const daysToAdd = (targetIdx - todayIdx + 7) % 7;
              startDate = moment().add(daysToAdd, 'days');
            }

            return {
              frequency: 'Bi-Weekly',
              repeatOn: repeatOn,
              startDate: startDate.toDate(),
              displayText: `Every other ${dayName} starting ${startDate.format('MMM D')}`
            };
          }
        },
        {
          regex: /\b(every|each)\s+(month|monthly)\b/i,
          parse: () => {
            const today = moment();
            const dayOfMonth = today.date();

            return {
              frequency: 'Monthly',
              repeatOnDay: dayOfMonth,
              startDate: today.toDate(),
              displayText: `Monthly on day ${dayOfMonth} starting ${today.format('MMM D')}`
            };
          }
        },
        // Detect weekly pattern with specific days
        {
          regex: /\b(every|each)\s+(week|weekly)\s+on\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i,
          parse: (matches) => {
            const dayName = matches[3].toLowerCase();
            const dayMapping = {
              monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
              thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
            };
            const repeatOn = dayMapping[dayName];

            // Find the next occurrence of this day
            const today = moment().format('ddd');
            let startDate;

            if (today === repeatOn) {
              startDate = moment();
            } else {
              const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              const todayIdx = daysOfWeek.indexOf(today);
              const targetIdx = daysOfWeek.indexOf(repeatOn);
              const daysToAdd = (targetIdx - todayIdx + 7) % 7;
              startDate = moment().add(daysToAdd, 'days');
            }

            return {
              frequency: 'Weekly',
              repeatOn: repeatOn,
              startDate: startDate.toDate(),
              displayText: `Weekly on ${dayName} starting ${startDate.format('MMM D')}`
            };
          }
        },
        // Handle specific "every/each month on the X" patterns
        {
          regex: /\b(every|each)\s+(month|monthly)\s+on\s+(?:the\s+)?(\d+)(?:st|nd|rd|th)?\b/i,
          parse: (matches) => {
            const dayOfMonth = parseInt(matches[3], 10);
            let startDate = moment();

            // If today's day is after the specified day, set to next month
            if (startDate.date() > dayOfMonth) {
              startDate = startDate.add(1, 'month');
            }

            // Set to the specified day of the month
            startDate = startDate.date(dayOfMonth);

            return {
              frequency: 'Monthly',
              repeatOnDay: dayOfMonth,
              startDate: startDate.toDate(),
              displayText: `Monthly on day ${dayOfMonth} starting ${startDate.format('MMM D')}`
            };
          }
        }
      ];

      // Try each pattern
      for (const pattern of recurringPatterns) {
        const matches = content.match(pattern.regex);
        if (matches) {
          // console.log('Recurring pattern matched:', matches);
          const parsed = pattern.parse(matches);

          result.isRecurring = true;
          result.pattern = {
            frequency: parsed.frequency,
            repeatOn: parsed.repeatOn,
            repeatOnDay: parsed.repeatOnDay
          };
          result.startDate = parsed.startDate;
          result.displayText = parsed.displayText;
          result.rawText = matches[0];
          break;
        }
      }

      return result;
    },

    /*

      Important Note: We're allowing OUTSOURCED employees to be able to see ALL THE TASKS in the office even if it's not related to them. This could be a security risks. We may want to introduce
      a setting for admin to be able to toggle this ON or OFF in future

    */

    async fetchToDos () {
      try {
        this.busy = true;

        // Set a timeout to ensure busy is reset after a reasonable amount of time
        // in case something goes wrong with the API call
        const busyResetTimeout = setTimeout(() => {
          if (this.busy) {
            // console.log('Busy flag timeout triggered - resetting');
            this.busy = false;
          }
        }, 10000); // 10 seconds max loading time

        if(
          (this.myCreatedTasks && this.myCreatedTasks.length === 0) ||
          (this.assignedToMeTasks && this.assignedToMeTasks.length === 0) ||
          (this.myEmployeesTasks && this.myEmployeesTasks.length === 0)
        ) {
          if(this.user.Role === 'Super Admin' || this.user.Role === 'Admin') // MODIFIED: Removed 'Outsourced'
            await this.getTasksForSite({siteID: this.siteID, employeeID: this.userID});
          else
            await this.getTasksForEmployee({siteID: this.siteID, employeeID: this.userID});
        }

        clearTimeout(busyResetTimeout);
        this.busy = false;
      } catch (error) {
        console.error('Error fetching tasks:', error);
        this.busy = false;
        this.$toast.error(error.message || 'Failed to load tasks');
      } finally {
        // Ensure busy is always set to false when done
        this.busy = false;
      }
    },

    onComposerSaving(task) {
      // Optimistic bookkeeping BEFORE the store write lands (saveTodo did this pre-2026-09-02,
      // ToDo.vue:1431-1434). Pushing AFTER the save races the Firestore snapshot — it has
      // already replaced these arrays, so a late push leaves an id-less duplicate lingering.
      this.myCreatedTasks.push(task);
      if (Array.isArray(task.assignees) && task.assignees.includes(this.userID)) this.assignedToMeTasks.push(task);
    },

    onComposerSaved(taskId) {
      if (taskId) this.scrollToAndHighlightTask(taskId);
    },

    /**
     * Scroll to a newly added task and highlight it briefly
     * @param {string} taskId - The ID of the task to scroll to
     */
    scrollToAndHighlightTask(taskId) {
      // Set the newly added task ID to trigger highlight class
      this.newlyAddedTaskId = taskId;

      // Wait for Vue to update the DOM with the new task
      this.$nextTick(() => {
        // Small delay to ensure Firestore listener has updated allTasks
        setTimeout(() => {
          // Find the task element by data attribute
          const taskElement = document.querySelector(`[data-task-id="${taskId}"]`);

          if (taskElement) {
            // Scroll the element into view smoothly
            taskElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }

          // Clear the highlight after animation completes (5s)
          setTimeout(() => {
            this.newlyAddedTaskId = null;
          }, 5000);
        }, 300); // Wait 300ms for Firestore update to propagate
      });
    },

    resetSearch() {
      // clear the search input. I have multiple b/c I have a different one for mobile.
      document
        .querySelectorAll('.ais-SearchBox-input')
        .forEach((e) => (e.value = ''))

      // clear all the hits
      document.querySelectorAll('.ais-Hits-item').forEach((e) => e.remove())

      // also clear the hits from the component, otherwise subsequent searches throw exceptions
      if(this.$refs.searchHits)
        this.$refs.searchHits.state.hits = [];

      if(this.$refs.commentsHits)
        this.$refs.commentsHits.state.hits = [];
      this.search = "";
    },

    async opentaskdetails(taskID) {
      //console.log(`Got ${taskID} in opentaskdetails`);
      try {
        // REMOVED: this.busy = true; // Don't show main overlay when opening panel
        // Find the task index to update its loading status
        const taskIndex = this.allTasks.findIndex(task => task.id === taskID);
        if (taskIndex !== -1) {
          // Set the loading status to true for this task
          this.$set(this.allTasksLoadingStatus, taskIndex, true);
        }

        this.editInProgress = true;
        this.resetSearch();

        // Make sure wiki pages are loaded before opening task details
        if (!this.wikiPages || this.wikiPages.length === 0) {
          //console.log('Wiki pages not loaded yet, fetching them before opening task details');
          try {
            let results = await this.getWikiPages({siteID: this.siteID});
            if (results && results.length > 0) {
              this.wikiPages = results[0] || [];
              //console.log('Wiki pages loaded for task details:', this.wikiPages);
            }
          } catch (error) {
            console.error('Error fetching wiki pages before opening task details:', error);
            this.wikiPages = [];
          }
        }

        // Get the task details
        let task = await this.getTaskDetails({taskID: taskID});

        // *** NEW: Check if task was found ***
        if (!task) {
          // If task is null (not found or error), show toast and exit
          this.$toast.error('Task not found or could not be loaded.');
          if (taskIndex !== -1) {
            this.$set(this.allTasksLoadingStatus, taskIndex, false); // Reset loading
          }
          this.editInProgress = false;
          return; // Stop execution here
        }
        // *** END NEW ***

        // Reset the loading status when task details are retrieved (only if task was found)
        if (taskIndex !== -1) {
          this.$set(this.allTasksLoadingStatus, taskIndex, false);
        }

        // *** REMOVED: Tag extraction from content. getTaskDetails now provides the tags array. ***
        // Log the tags received from getTaskDetails
        // console.log(`Tags received from getTaskDetails for task ${taskID}:`, task.tags);

        //console.log('Opening task details with wiki pages:', this.wikiPages);

        const panel1Handle = this.$showPanel({
          component : TodoDetails,
          openOn: 'right',
          width: 750,
          props: {
            task: task,
            wikiPages: this.wikiPages,
            patients: this.patients,
            siteTags: this.computedAvailableTags, // Pass computed task tags
          },
          listeners: {
            'task-updated': (updatedTask) => { // Keep this listener
              // console.log('Task was updated, refreshing task in list:', updatedTask);
              // Update the task in all relevant arrays
              this.updateTaskInLists(updatedTask);
            }
          }
        })

        panel1Handle.promise
          .then(result => {
            //console.log(`panel handle promise done`);
            // Panel closed successfully
            this.editInProgress = false;
          });
          // REMOVED: .finally block that set this.busy = false
      } catch(error) {
        this.editInProgress = false;
        // Ensure busy is false if an error occurs before panel promise resolves/rejects
        // (Though it shouldn't be set to true in this flow anymore)
        // busy = false is handled in the finally block of the promise
        console.log(`Error while trying to edit task: ${error}`);
      }
    },

    // Helper method to update a task in all relevant arrays
    updateTaskInLists(updatedTask) {
      // Update in allTasks array
      const allTasksIndex = this.allTasks.findIndex(t => t.id === updatedTask.id);
      if (allTasksIndex !== -1) {
        this.$set(this.allTasks, allTasksIndex, {...updatedTask});
      }

      // Update in myCreatedTasks if present
      const myCreatedIndex = this.myCreatedTasks.findIndex(t => t.id === updatedTask.id);
      if (myCreatedIndex !== -1) {
        this.$set(this.myCreatedTasks, myCreatedIndex, {...updatedTask});
      }

      // Update in assignedToMeTasks if present
      const assignedIndex = this.assignedToMeTasks.findIndex(t => t.id === updatedTask.id);
      if (assignedIndex !== -1) {
        this.$set(this.assignedToMeTasks, assignedIndex, {...updatedTask});
      }

      // Update in myEmployeesTasks if present
      const employeesIndex = this.myEmployeesTasks.findIndex(t => t.id === updatedTask.id);
      if (employeesIndex !== -1) {
        this.$set(this.myEmployeesTasks, employeesIndex, {...updatedTask});
      }
    },

    async editItem(item, index, taskType) {
      //console.log('i am in edit item', item, index, taskType);
      let temp;
      let selectedType;
      switch(taskType) {
        case 'mytasks': temp = this.myTaskLoadingStatus; selectedType = this.myTaskSelectedStatus; break;
        case 'assignedtome': temp = this.assignedToMeTaskLoadingStatus; selectedType = this.assignedToMeTaskSelectedStatus; break;
        case 'myemployeestasks': temp = this.myEmployeesTaskLoadingStatus; selectedType = this.myEmployeesTaskSelectedStatus; break;
      }

      Vue.set(temp, index, true);
      Vue.set(selectedType, index, false);

      let task;
      try {
        // Make sure wiki pages are loaded before opening task details
        if (!this.wikiPages || this.wikiPages.length === 0) {
          //console.log('Wiki pages not loaded yet, fetching them before editing task');
          try {
            let results = await this.getWikiPages({siteID: this.siteID});
            if (results && results.length > 0) {
              this.wikiPages = results[0] || [];
              //console.log('Wiki pages loaded for task edit:', this.wikiPages);
            }
          } catch (error) {
            console.error('Error fetching wiki pages before editing task:', error);
            this.wikiPages = [];
          }
        }

        task = await this.getTaskDetails({taskID: item.id});
        //console.log(`task: ${JSON.stringify(task)}`);

        if(!task.assignees) {
          let temp2 = [];
          temp2.push(task.assignedToEmpID);
          Vue.set(task, 'assignees', temp2);
        }

        /*
          Adding a patch because I noticed sometimes, some sites have some tasks that have wrong completedAt date and that messes up when they try to mark them completed
        */
        if(item.completedAt) {
          try {
            let temp = item.completedAt.toISOString();
            //console.log('able to convert completedAt to ISO String. No issue', temp);
          } catch (error) {
            // if we catch an error here then we have an invalid date here. let's remove it
            delete item.completedAt;
            delete item.completedAtStr;
          }
        }

        this.resetSearch();

        console.log('Opening task editor with wiki pages:', this.wikiPages);

        const panel1Handle = this.$showPanel({
          component : TodoDetails,
          openOn: 'right',
          width: 750,
          props: {
            task: task,
            wikiPages: this.wikiPages,
            patients: this.patients,
          },
          listeners: {
            'task-updated': (updatedTask) => {
              // console.log('Task was updated, refreshing task in list:', updatedTask);
              // Find and update the task in our local array
              const taskIndex = this.allTasks.findIndex(t => t.id === updatedTask.id);
              if (taskIndex !== -1) {
                // Update the task in the array with the latest data
                this.$set(this.allTasks, taskIndex, updatedTask);
              }
            }
          }
        })

        panel1Handle.promise
          .then(result => {
            //console.log(`panel handle promise done`);
            Vue.set(temp, index, false);
        });
      } catch(error) {
        console.log(`Error while trying to edit task: ${error}`);
        Vue.set(temp, index, true);
      }
    },

    /**
     * Saves the todo item, on each change
     * @param {TodoItem} item
     */
    async handleItemChange (item) {
      // console.log('in handleItemChange', item);
      /*
        Adding a patch because I noticed sometimes, some sites have some tasks that have wrong completedAt date and that messes up when they try to mark them completed
      */
      if(item.completedAt) {
        try {
          let temp = item.completedAt.toISOString();
          // console.log('able to convert completedAt to ISO String. No issue', temp);
        } catch (error) {
          // if we catch an error here then we have an invalid date here. let's remove it
          delete item.completedAt;
          delete item.completedAtStr;
        }
      }
      await this.updateTask(item);
      //ToDoService.updateItem(item.id, item);
      //this.fetchToDos();
    },

    async deleteItem(item, index, taskType) {
      // console.log(`trying to delete all instances of an item`, item);
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;

      let temp;
      let selectedType;

      switch(taskType) {
        case 'mytasks': temp = this.myTaskLoadingStatus; selectedType = this.myTaskSelectedStatus; break;
        case 'assignedtome': temp = this.assignedToMeTaskLoadingStatus; selectedType = this.assignedToMeTaskSelectedStatus; break;
        case 'myemployeestasks': temp = this.myEmployeesTaskLoadingStatus; selectedType = this.myEmployeesTaskSelectedStatus; break;
        case 'alltasks': temp = this.allTasksLoadingStatus; selectedType = this.allTasksSelectedStatus; break;
      }

      // Only set status if temp and selectedType are defined
      if (temp && selectedType) {
        Vue.set(temp, index, true);
        Vue.set(selectedType, index, false);
      }

      if(item.id) {

        // --- Permission Check Start ---
        const deletePermissionSetting = this.allSettings?.TASKS_WHO_CAN_DELETE?.PropertyValue?.toLowerCase() || 'creator'; // Default to creator if setting is missing
        const isCreator = item.createdByEmpID === this.userID;
        const isSuperAdmin = this.user?.Role === 'Super Admin';

        if (deletePermissionSetting === 'creator' && !isCreator && !isSuperAdmin) {
          this.$toast.error('You do not have permission to delete this task. Only the creator or a Super Admin can delete it.');
          // Reset loading state if applicable
          if (temp) {
            Vue.set(temp, index, false);
            Vue.set(selectedType, index, true); // Optionally reset selected status too
          }
          // Reset spinner states before returning
          this.busy = false;
          this.isDeletingSingle = false;
          return; // Stop execution
        }
        // --- Permission Check End ---

        // Proceed with deletion if permission check passed
        try {
          await this.deleteTask({
            id: item.id,
            deleted: true,
            deletedBy: this.userID,
            deletedOn: moment().toDate(),
            // Pass along the permanentDelete flag if it exists
            permanentDelete: item.permanentDelete || false
          });
        } catch (error) {
          // Handle potential errors from the deleteTask action itself
          console.error("Error calling deleteTask action:", error);
          this.$toast.error('An error occurred while trying to delete the task.');
        } finally {
          // Only set loading state to false if temp exists
          if (temp) {
            Vue.set(temp, index, false);
            // Don't reset selectedType here, let the UI reflect the deletion
           this.busy = false;
           this.isDeletingSingle = false;
          }
        }

      }
      else {
        this.$toast.error('Something went wrong. This task does not have an ID to be deleted. Please try again');
        console.error(`Having issues trying to delete task: ${JSON.stringify(item)}`);
        // LogRocket.log(`Having issues trying to delete task: ${JSON.stringify(item)}`);
        // Reset loading state if applicable
        if (temp) {
          Vue.set(temp, index, false);
          Vue.set(selectedType, index, true);
         this.busy = false;
         this.isDeletingSingle = false;
        }
      }
    },

    async deleteThisInstanceOnly(item) {
      // console.log(`trying to delete only this instance of item`, item);
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;
      this.busy = true;
      this.isDeletingSingle = true;

      // --- Permission Check Start ---
      const deletePermissionSetting = this.allSettings?.TASKS_WHO_CAN_DELETE?.PropertyValue?.toLowerCase() || 'creator'; // Default to creator
      const isCreator = item.createdByEmpID === this.userID;

      if (deletePermissionSetting === 'creator' && !isCreator) {
        this.$toast.error(
          'You do not have permission to modify this recurring task instance. Only the creator can perform this action.'
        );
        // Reset spinner states before returning
        this.busy = false;
        this.isDeletingSingle = false;
        return; // Stop execution
      }
      // --- Permission Check End ---

      // Proceed with deleting this instance if permission check passed
      let payload = {
        task : item,
        clientDateFormat : (this.allSettings?.GENERAL_TIMEZONE?.PropertyValue || 'America/New_York').toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'
      }
      //console.log(`payload: ${JSON.stringify(payload)}`);
      try {
        await this.deleteThisInstanceOfRepeatedTask(payload);
      } catch (error) {
       this.busy = false; // Hide overlay on error
       this.isDeletingSingle = false;
        // Handle potential errors from the action
        console.error("Error calling deleteThisInstanceOfRepeatedTask action:", error);
        this.$toast.error('An error occurred while trying to delete this task instance.');
     } finally {
       // Ensure flags are reset even if no error (though action might not throw)
       this.busy = false;
       this.isDeletingSingle = false;
     }
    },

    async deleteAllCompleted() {
      // *** NEW: Prevent deletion when viewing a single task ***
      if (this.taskID) {
        console.log('Task ID present, skipping deleteAllCompleted action.');
        return;
      }
      // *** END NEW ***

      // --- Permission Warning Check Start ---
      const deletePermissionSetting = this.allSettings?.TASKS_WHO_CAN_DELETE?.PropertyValue?.toLowerCase() || 'creator'; // Default to creator
      const isSuperAdmin = this.user?.Role === 'Super Admin';
      let showWarning = false;

      if (deletePermissionSetting === 'creator' && !isSuperAdmin) {
        const completedTasks = this.allTasks.filter(task => task.status === 'COMPLETED');
        const hasCompletedTasksNotCreatedByUser = completedTasks.some(task => task.createdByEmpID !== this.userID);
        if (hasCompletedTasksNotCreatedByUser) {
          showWarning = true;
        }
      }

      if (showWarning) {
         this.$toast.warning(
            "Warning: Task deletion set to 'Creator Only'. Proceeding will only delete completed tasks created by you.",
            { duration: 5000 } // Optional: Longer duration for warning
         );
      }
      // --- Permission Warning Check End ---

      // Proceed with the actual deletion logic regardless of the warning
      this.busy = true;
      this.isDeleting = true; // Use the specific deleting flag

      let payload = {
        siteID: this.siteID,
        // NOTE: createdByEmpID is passed here, but the backend should ideally use deletedBy for permission checks if implemented there
        createdByEmpID: this.userID,
        deletedBy: this.userID,
        deletedOn: moment().toDate()
      }
      // console.log('deleteAllCompleted payload', payload);
      try {
        await this.deleteAllCompletedTasks(payload);
        // Success toast is shown within the action or here if preferred
        // We might not need a success toast here if the backend takes time and the list updates visually
        // this.$toast.success("Initiated deletion of completed tasks.");
      } catch (error) {
        // Error already handled by toast in action
        // console.error("Error during deleteAllCompleted call:", error);
      } finally {
        // Reset states regardless of success/failure, as the operation is async
        this.busy = false;
        this.isDeleting = false;
        // Reset filter view AFTER backend call finishes or listener updates view
        // Removed: this.createdByMeTasksView = 'Show all tasks'; // Property no longer used
        if (this.$refs.taskFilters) { // Reset dropdown visually
           this.$refs.taskFilters.selectedStatus = 'Show all tasks';
        }
      }
    },

    /**
     * Persists the list of todo items
     */
    storeItems () {
      ToDoService.storeAll(this.todoItems);
    },

    updateOrder() {
      //console.dir(this.todoItems);
      //this.updateMultipleTasks(items);
    },

    updateMyTasksInclude() {
      // console.log('here');
    },

    // New method to sort tasks by status and due date
    sortTasksByStatusAndDueDate(tasks) {
      if (!tasks || tasks.length === 0) return [];

      // Create a copy of the tasks array to avoid mutating the original
      const tasksCopy = [...tasks];

      // Apply custom sorting if selected
      if (this.taskSortOption !== 'default') {
        return this.applySorting(tasksCopy);
      }

      // Default sorting: Incomplete first, then completed
      return tasksCopy.sort((a, b) => {
        // First sort by status (INCOMPLETE comes first)
        if (a.status !== b.status) {
          return a.status === 'COMPLETED' ? 1 : -1;
        }

        // If both tasks have the same status, sort by due date
        if (a.status === 'INCOMPLETE') {
          // For incomplete tasks, sort by due date (earliest first)
          // Safely handle dates that might be strings or Date objects
          const dateA = a.dueAt ? moment(a.dueAt).valueOf() : 0;
          const dateB = b.dueAt ? moment(b.dueAt).valueOf() : 0;
          return dateA - dateB;
        } else {
          // For completed tasks, sort by completion date (latest first)
          const dateA = a.completedAt ? moment(a.completedAt).valueOf() :
                        a.dueAt ? moment(a.dueAt).valueOf() : 0;
          const dateB = b.completedAt ? moment(b.completedAt).valueOf() :
                        b.dueAt ? moment(b.dueAt).valueOf() : 0;
          return dateB - dateA;
        }
      });
    },

    // Custom sorting method based on the selected option
    applySorting(tasks) {
      const getSortedValues = (arr) => [...(arr || [])].sort();
      const compareValues = (a, b, isDesc = false) => isDesc ?
        b.toLowerCase().localeCompare(a.toLowerCase()) :
        a.toLowerCase().localeCompare(b.toLowerCase());

      const compareArrays = (arrA, arrB, isDesc = false) => {
        const sortedA = getSortedValues(arrA);
        const sortedB = getSortedValues(arrB);

        // Compare first values
        const firstA = sortedA[0] || '';
        const firstB = sortedB[0] || '';
        if (firstA !== firstB) {
          return compareValues(firstA, firstB, isDesc);
        }

        // If first values are equal, compare second values
        const secondA = sortedA[1] || '';
        const secondB = sortedB[1] || '';
        return compareValues(secondA, secondB, isDesc);
      };

      switch(this.taskSortOption) {
        case 'desc-asc':
          return tasks.sort((a, b) => {
            const textA = this.cleanContent(a.content).text;
            const textB = this.cleanContent(b.content).text;
            return compareValues(textA, textB);
          });

        case 'desc-desc':
          return tasks.sort((a, b) => {
            const textA = this.cleanContent(a.content).text;
            const textB = this.cleanContent(b.content).text;
            return compareValues(textA, textB, true);
          });

        case 'creator-asc':
          return tasks.sort((a, b) => {
            const creatorA = this.getEmployeeName(a.createdByEmpID) || '';
            const creatorB = this.getEmployeeName(b.createdByEmpID) || '';
            return compareValues(creatorA, creatorB);
          });

        case 'creator-desc':
          return tasks.sort((a, b) => {
            const creatorA = this.getEmployeeName(a.createdByEmpID) || '';
            const creatorB = this.getEmployeeName(b.createdByEmpID) || '';
            return compareValues(creatorA, creatorB, true);
          });

        case 'tags-asc':
          return tasks.sort((a, b) => {
            const tagsA = this.cleanContent(a.content).tags;
            const tagsB = this.cleanContent(b.content).tags;
            return compareArrays(tagsA, tagsB);
          });

        case 'tags-desc':
          return tasks.sort((a, b) => {
            const tagsA = this.cleanContent(a.content).tags;
            const tagsB = this.cleanContent(b.content).tags;
            return compareArrays(tagsA, tagsB, true);
          });

        case 'patient-asc':
          return tasks.sort((a, b) => {
            const patientsA = this.cleanContent(a.content).patients.map(p => p.name);
            const patientsB = this.cleanContent(b.content).patients.map(p => p.name);
            return compareArrays(patientsA, patientsB);
          });

        case 'patient-desc':
          return tasks.sort((a, b) => {
            const patientsA = this.cleanContent(a.content).patients.map(p => p.name);
            const patientsB = this.cleanContent(b.content).patients.map(p => p.name);
            return compareArrays(patientsA, patientsB, true);
          });

        case 'status-asc':
          // MODIFIED: Sort based on Overdue -> Incomplete -> Completed
          return tasks.sort((a, b) => {
            return this.getSortableStatusValue(a) - this.getSortableStatusValue(b);
          });

        case 'status-desc':
          // MODIFIED: Sort based on Completed -> Incomplete -> Overdue
          return tasks.sort((a, b) => {
            return this.getSortableStatusValue(b) - this.getSortableStatusValue(a);
          });

        case 'date-desc':
          return tasks.sort((a, b) => {
            const dateA = a.dueAt ? moment(a.dueAt).valueOf() : 0;
            const dateB = b.dueAt ? moment(b.dueAt).valueOf() : 0;
            return dateB - dateA;
          });

        case 'date-asc':
          return tasks.sort((a, b) => {
            const dateA = a.dueAt ? moment(a.dueAt).valueOf() : 0;
            const dateB = b.dueAt ? moment(b.dueAt).valueOf() : 0;
            return dateA - dateB;
          });

        case 'assignee-asc':
          return tasks.sort((a, b) => {
            const assigneeA = a.assignedToEmpID || '';
            const assigneeB = b.assignedToEmpID || '';
            return assigneeA.localeCompare(assigneeB);
          });

        case 'assignee-desc':
          return tasks.sort((a, b) => {
            const assigneeA = a.assignedToEmpID || '';
            const assigneeB = b.assignedToEmpID || '';
            return assigneeB.localeCompare(assigneeA);
          });

        case 'repeats-asc':
          return tasks.sort((a, b) => {
            const repeatA = this.getRepeatSortValue(a);
            const repeatB = this.getRepeatSortValue(b);
            return compareValues(repeatA, repeatB);
          });

        case 'repeats-desc':
          return tasks.sort((a, b) => {
            const repeatA = this.getRepeatSortValue(a);
            const repeatB = this.getRepeatSortValue(b);
            return compareValues(repeatA, repeatB, true);
          });

        case 'creator':
          newSort = currentSort === 'creator-asc' ? 'creator-desc' : 'creator-asc';
          break;
        // NEW: Add case for repeats sort toggle
        case 'repeats':
          newSort = currentSort === 'repeats-asc' ? 'repeats-desc' : 'repeats-asc';
          break;
        default:
          newSort = 'default';
      }

      this.taskSortOption = newSort;
    },

    // Method to clean content and extract tags, patients, and text
    cleanContent(content) {
      if (!content) return { text: '', tags: [], patients: [], employees: [] };

      // Create a temporary div to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      // Initialize result object
      const result = {
        text: '',
        employees: [],
        patients: [],
        tags: []
      };

      // Function to process text nodes
      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent.trim();
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if this is a badge
          if (node.classList.contains('badge')) {
            if (node.hasAttribute('data-teamio-employeeid')) {
              const employeeId = node.getAttribute('data-teamio-employeeid');
              const employeeName = node.textContent.trim();
              result.employees.push({ id: employeeId, name: employeeName });
              return '';
            }
            if (node.hasAttribute('data-teamio-patientid')) {
              const patientId = node.getAttribute('data-teamio-patientid');
              const patientName = node.textContent.trim();
              result.patients.push({ id: patientId, name: patientName });
              return '';
            }
            // If it's a badge but not an employee or patient, it's a tag
            const tagText = node.textContent.trim();
            if (tagText) {
              result.tags.push(tagText);
            }
            return '';
          }

          // Process child nodes
          return Array.from(node.childNodes)
            .map(processNode)
            .filter(text => text)
            .join(' ');
        }

        return '';
      };

      // Process all nodes and get text content
      result.text = processNode(tempDiv);

      // Clean up extra whitespace in text
      result.text = result.text.replace(/\s+/g, ' ').trim();

      return result;
    },

    // Helper method for basic text extraction
    extractTextFromHtml(html) {
      return this.cleanContent(html).text;
    },

    handleSort(column) {
      const currentSort = this.taskSortOption;
      let newSort;

      switch (column) {
        case 'desc':
          newSort = currentSort === 'desc-asc' ? 'desc-desc' : 'desc-asc';
          break;
        case 'patient':
          newSort = currentSort === 'patient-asc' ? 'patient-desc' : 'patient-asc';
          break;
        case 'tags':
          newSort = currentSort === 'tags-asc' ? 'tags-desc' : 'tags-asc';
          break;
        case 'status':
          newSort = currentSort === 'status-asc' ? 'status-desc' : 'status-asc';
          break;
        case 'date':
          newSort = currentSort === 'date-asc' ? 'date-desc' : 'date-asc';
          break;
        case 'assignee':
          newSort = currentSort === 'assignee-asc' ? 'assignee-desc' : 'assignee-asc';
          break;
        case 'creator':
          newSort = currentSort === 'creator-asc' ? 'creator-desc' : 'creator-asc';
          break;
        case 'repeats':
          newSort = currentSort === 'repeats-asc' ? 'repeats-desc' : 'repeats-asc';
          break;
        default:
          newSort = 'default';
      }

      this.taskSortOption = newSort;
    },

    // Quick filter for tasks due today
    // Toggleable quick filter for today's tasks
    toggleTodayFilter() {
      if (this.isTodayActive) {
        // Deactivate today's filter
        this.isTodayActive = false;
        // Remove due date filter, keep other filters
        this.handleFilterChange({
          ...this.activeFilters,
          dueDate: null,
          dueToday: false
        });
        if (this.$refs.taskFilters) {
          this.$refs.taskFilters.selectedDueDate = null;
        }
      } else {
        // Activate today's filter
        this.isTodayActive = true;
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        const todayStr = `${yyyy}-${mm}-${dd}`;
        // Use current assignees if set, otherwise empty
        const assignees = this.activeFilters.assignees && this.activeFilters.assignees.length > 0
          ? this.activeFilters.assignees
          : [];
        this.handleFilterChange({ ...this.activeFilters, taskFilter: 'Show all tasks', dueDate: todayStr, assignees, dueToday: true });
        if (this.$refs.taskFilters) {
          this.$refs.taskFilters.selectedTaskFilter = 'Show all tasks';
          this.$refs.taskFilters.selectedDueDate = today;
        }
      }
    },

    // Toggleable quick filter for tasks assigned to the current user
    toggleAssignedToMeFilter() {
      if (this.isAssignedToMeActive) {
        // Deactivate assigned to me filter
        this.isAssignedToMeActive = false;
        // Remove assignee filter, keep other filters
        this.handleFilterChange({
          ...this.activeFilters,
          assignees: [],
        });
        if (this.$refs.taskFilters) {
          this.$refs.taskFilters.selectedAssignees = [];
        }
      } else {
        // Activate assigned to me filter
        this.isAssignedToMeActive = true;
        let userObj = null;
        if (this.activeEmployees && Array.isArray(this.activeEmployees)) {
          userObj = this.activeEmployees.find(e => e.EmployeeID === this.userID);
        }
        if (!userObj) {
          userObj = { id: this.userID, name: this.user?.Name || "Me" };
        } else {
          userObj = { id: userObj.EmployeeID, name: userObj.Name, avatar: userObj.PictureUrl };
        }
        const dueDate = this.activeFilters.dueDate || null;
        this.handleFilterChange({ ...this.activeFilters, taskFilter: 'Show all tasks', assignees: [userObj], dueDate });
        if (this.$refs.taskFilters) {
          this.$refs.taskFilters.selectedTaskFilter = 'Show all tasks';
          this.$refs.taskFilters.selectedAssignees = [userObj];
          if (dueDate) {
            this.$refs.taskFilters.selectedDueDate = new Date(dueDate);
          }
        }
      }
    },

    handleFilterChange(filters) {
      // *** NEW: Prevent filter actions when viewing a single task ***
      if (this.taskID) {
        // Skip filter change handler when task ID is present
        return;
      }

      // Reset all filter flags first, preserving specific filters like patients, assignees, tags, dueDate
      this.activeFilters = {
        ...this.activeFilters, // Keep existing patient/assignee/tag/date filters
        showIncomplete: false,
        showOverdue: false,
        showNewComments: false,
        showRepeating: false,
        showNonRepeating: false,
        showCompletedOnly: false,
        dueToday: false
      };

      // Map the taskFilter string to the appropriate boolean flags
      if (filters.taskFilter) {
        switch (filters.taskFilter) {
          case 'Show incomplete tasks only':
            this.activeFilters.showIncomplete = true;
            break;
          case 'Show overdue tasks only':
            this.activeFilters.showOverdue = true;
            break;
          case 'Show tasks with new comments':
            this.activeFilters.showNewComments = true;
            break;
          case 'Show repeating tasks only':
            this.activeFilters.showRepeating = true;
            break;
          case 'Show non-repeating tasks only':
            this.activeFilters.showNonRepeating = true;
            break;
          case 'Show all completed tasks':
            this.activeFilters.showCompletedOnly = true;
            // Load completed tasks when this filter is selected
            this.getCompletedTasksForSite({ siteID: this.siteID });
            break;
          case 'Delete all completed tasks':
            // Trigger the deleteAllCompleted method
            this.deleteAllCompleted();
            // Reset filter to 'Show all tasks' after action is triggered
            if (this.$refs.taskFilters) {
              this.$refs.taskFilters.selectedTaskFilter = 'Show all tasks';
            }
            break;
          case 'Show all tasks':
            // All flags remain false for show all
            break;
        }
      }

      // Apply the new filters
      if (filters.patients && Array.isArray(filters.patients)) {
        this.activeFilters.patients = [...filters.patients];
      }

      if (filters.assignees && Array.isArray(filters.assignees)) {
        this.activeFilters.assignees = [...filters.assignees];
      }

      if (filters.creators && Array.isArray(filters.creators)) {
        this.activeFilters.creators = [...filters.creators];
      }

      if (filters.tags && Array.isArray(filters.tags)) {
        this.activeFilters.tags = [...filters.tags];
      }

      // NEW: Handle exclude tags
      if (filters.excludeTags && Array.isArray(filters.excludeTags)) {
        this.activeFilters.excludeTags = [...filters.excludeTags];
      } else {
        this.activeFilters.excludeTags = [];
      }

      // Always update dueDate from the incoming filters, even if null
      this.activeFilters.dueDate = filters.dueDate;
      if (filters.dueDate && moment(filters.dueDate).isSame(moment(), 'day')) {
        this.activeFilters.dueToday = true;
      }

      // Force update to ensure the UI reflects the changes
      this.$forceUpdate();
    },

    clearAllFiltersAndReset() {
      console.log('Todo - clearAllFiltersAndReset called');

      // Reset the activeFilters structure
      this.activeFilters = {
        patients: [],
        assignees: [],
        creators: [], // Add creators array
        tags: [],
        excludeTags: [], // NEW: Tags to exclude
        showIncomplete: false,
        showOverdue: false,
        showRepeating: false,
        showNonRepeating: false,
        showCompletedOnly: false,
        dueToday: false,
        showNewComments: false
      };

      console.log('Todo - activeFilters reset:', JSON.stringify(this.activeFilters));

      // Reset the filters in the child component
      if (this.$refs.taskFilters) {
        console.log('Todo - calling clearFilters on taskFilters component');
        this.$refs.taskFilters.clearFilters();
      } else {
        console.log('Todo - taskFilters ref not found');
      }

      // Force update to ensure the UI reflects the changes
      this.$forceUpdate();
      console.log('Todo - clearAllFiltersAndReset completed');
    },

    handleSearch(search) {
      this.search = search;
    },

    filterTasks(tasks) {
      if (!tasks) return [];

      const filteredTasks = tasks.filter(task => {
        // Get task's patients, assignees and tags
        const taskPatients = this.cleanContent(task.content).patients.map(p => p.id);
        const taskAssignees = task.assignees || [];
        const taskCreator = task.createdByEmpID;

        // Extract tags from content HTML
        const contentTags = this.cleanContent(task.content).tags;

        // Get tags from the task object's tags array (ensure it's an array)
        const objectTags = Array.isArray(task.tags) ? task.tags : [];

        // Combine both sets of tags, ensuring uniqueness
        const allTags = Array.from(new Set([...contentTags, ...objectTags]));

        // Check if task matches all active filters
        const matchesPatients = this.activeFilters.patients.length === 0 ||
          this.activeFilters.patients.some(p => {
            const patientId = String(p.id);
            return taskPatients.some(id => String(id) === patientId);
          });

        // Robust assignee matching (id, EmployeeID, assignedToEmpID)
        const matchesAssignees = this.activeFilters.assignees.length === 0 ||
          this.activeFilters.assignees.some(a => {
            const filterId = String(a.id || a.EmployeeID);
            return (taskAssignees.some(id => String(id) === filterId)) ||
                   (String(task.assignedToEmpID) === filterId);
          });

        // Creator matching
        const matchesCreator = this.activeFilters.creators.length === 0 ||
          this.activeFilters.creators.some(c => String(c.id) === String(taskCreator));

        // Check against the combined list of actual tags
        const matchesTags = this.activeFilters.tags.length === 0 ||
          this.activeFilters.tags.some(tagFilter => allTags.includes(tagFilter));

        // NEW: Check if task should be excluded based on excluded tags
        // Task passes if: no exclude tags set, OR task doesn't have ANY of the excluded tags
        const notExcluded = !this.activeFilters.excludeTags ||
          this.activeFilters.excludeTags.length === 0 ||
          !this.activeFilters.excludeTags.some(tagFilter => allTags.includes(tagFilter));

        // Check if task matches the status filters
        let matchesStatus = true;
        if (this.activeFilters.showIncomplete) {
          matchesStatus = task.status === 'INCOMPLETE';
        } else if (this.activeFilters.showCompletedOnly) {
          matchesStatus = task.status === 'COMPLETED';
        }

        // Check if task matches repeat status
        let matchesRepeatStatus = true;
        if (this.activeFilters.showRepeating) {
          matchesRepeatStatus = !!task.repeat;
        } else if (this.activeFilters.showNonRepeating) {
          matchesRepeatStatus = !task.repeat;
        }

        // Check if task matches due date filters
        const matchesDueDate = !this.activeFilters.dueDate ||
          (task.dueAt && moment(task.dueAt).isSame(moment(this.activeFilters.dueDate), 'day'));

        // Check if task matches overdue filter
        const matchesOverdue = !this.activeFilters.showOverdue ||
          (this.activeFilters.showOverdue && task.status === 'INCOMPLETE' && moment(task.dueAt).isBefore(moment(), 'day'));

        // Check if task matches due today filter
        const matchesDueToday = !this.activeFilters.dueToday ||
          (this.activeFilters.dueToday && task.dueAt && moment(task.dueAt).isSame(moment(), 'day'));

        // Check if task has new comments for the current user
        const matchesNewComments = !this.activeFilters.showNewComments ||
          (this.activeFilters.showNewComments &&
           task.unreadCommentsList &&
           Array.isArray(task.unreadCommentsList) &&
           task.unreadCommentsList.includes(this.userID));

        return matchesStatus &&
               matchesPatients &&
               matchesAssignees &&
               matchesCreator &&
               matchesTags &&
               notExcluded && // NEW: Apply exclude tags filter
               matchesRepeatStatus &&
               matchesDueDate &&
               matchesOverdue &&
               matchesDueToday &&
               matchesNewComments;
      });

      return filteredTasks;
    },

    /**
     * Detects dates in the task text and updates the detectedDateInfo
     * @param {string} text - The text to parse for dates
     */
    detectDates(text) {
      if (!text) {
        this.resetDateInfo();
        return;
      }

      // Use Sugar parser now
      try {
        console.log('Detecting dates in:', text);

        // Call the new parse function from the default export
        const parsedResult = simpleParser.parse(text); // Use simpleParser.parse
        console.log('Sugar parsed dates:', parsedResult);

        if (parsedResult && parsedResult.date) { // Check if parse returned a result with a date

          this.detectedDateInfo = {
            hasDate: true,
            date: parsedResult.date, // Date from Sugar
            displayText: parsedResult.text, // Original text for now
            isRecurring: parsedResult.isRecurring, // Determined by regex check
            frequency: parsedResult.frequency, // Determined by regex check
            daysOfWeek: parsedResult.daysOfWeek, // Determined by regex check
            dayOfMonth: parsedResult.dayOfMonth, // Determined by regex check
            rawText: text, // Keep raw text
            isLastDayOfMonth: parsedResult.isLastDayOfMonth // Add isLastDayOfMonth
          };

          console.log('Date detected (Sugar + Recurrence Check):', this.detectedDateInfo);
        } else {
          this.resetDateInfo();
        }
      } catch (error) {
        console.error('Error detecting dates:', error);
        this.resetDateInfo();
      }
    },

    /**
     * Extracts recurring pattern information from text
     * @param {string} text - The task text
     * @returns {object|null} - Recurring pattern object or null
     */
    extractRecurringPattern(text) {
      const lowerText = text.toLowerCase();

      if (lowerText.includes('every day') || lowerText.includes('daily')) {
        return {
          frequency: 'daily'
        };
      }

      if (lowerText.includes('every week') || lowerText.includes('weekly')) {
        return {
          frequency: 'weekly'
        };
      }

      const weekdayMatch = lowerText.match(/every (monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
      if (weekdayMatch) {
        return {
          frequency: 'weekly',
          repeatOn: weekdayMatch[1].charAt(0).toUpperCase() + weekdayMatch[1].slice(1).toLowerCase()
        };
      }


      if (lowerText.includes('every month') || lowerText.includes('monthly')) {
        return {
          frequency: 'monthly'
        };
      }

      const monthDayMatch = lowerText.match(/every (\d+)(st|nd|rd|th)? of the month/i);
      if (monthDayMatch) {
        return {
          frequency: 'monthly',
          repeatOnDay: parseInt(monthDayMatch[1], 10)
        };
      }

      return null;
    },

    // Helper method to extract day of week from a match
    getDayOfWeekFromMatch(text) {
      const days = {
        'monday': 1,
        'tuesday': 2,
        'wednesday': 3,
        'thursday': 4,
        'friday': 5,
        'saturday': 6,
        'sunday': 0
      };

      const dayRegex = /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i;
      const match = text.match(dayRegex);

      if (match) {
        const day = match[1].toLowerCase();
        return [days[day]]; // Return as array for compatibility with Vue calendar
      }

      return []; // Default empty array if no day found
    },

    // *** NEW: Method to determine sortable status value ***
    getSortableStatusValue(task) {
      if (!task) return 0; // Handle null/undefined tasks
      if (task.status === 'COMPLETED') {
        return 3; // Completed last
      } else {
        // Check if overdue
        if (task.dueAt && moment(task.dueAt).isBefore(moment(), 'day')) {
          return 1; // Overdue first
        }
        return 2; // Incomplete second
      }
    },
    // *** END NEW ***

    // *** NEW: Method to get sortable value for repeat status ***
    getRepeatSortValue(task) {
      if (!task || !task.repeat) {
        // Return a value that sorts non-repeating tasks last when ascending
        return 'zNoRepeat';
      }

      let description = 'aRepeat - '; // Prefix to sort repeating tasks first
      switch (task.repeatFrequency) {
        case 'Daily': description += 'Daily'; break;
        case 'Weekly': description += `Weekly (${task.repeatOn || '?'})`; break;
        case 'Bi-Weekly': description += `Bi-Weekly (${task.repeatOn || '?'})`; break;
        case 'Monthly':
          if (task.repeatOnLastDayOfMonth) {
            description += 'Monthly (Last Day)';
          } else {
            description += `Monthly (Day ${task.repeatOnDay || '?'})`;
          }
          break;
        case 'Yearly':
          // Ensure month and day exist before formatting
          const month = task.repeatOnMonth || '?';
          const day = task.repeatOnDay || '?';
          description += `Yearly (${month}/${day})`;
          break;
        default: description += (task.repeatFrequency || 'Unknown'); break;
      }
      return description;
    },
    // *** END NEW ***

    // Add new methods for column filtering
    handleColumnFilter(column, value) {
      // Create a copy of current filters
      const newFilters = { ...this.activeFilters };

      switch (column) {
        case 'status':
          // Toggle between incomplete, completed, and all
          if (this.activeFilters.showIncomplete) {
            newFilters.showIncomplete = false;
            newFilters.showCompletedOnly = true;
          } else if (this.activeFilters.showCompletedOnly) {
            newFilters.showCompletedOnly = false;
          } else {
            newFilters.showIncomplete = true;
          }
          break;

        case 'patient':
          if (value) {
            // If the patient is already in the filter, remove it
            const existingIndex = newFilters.patients.findIndex(p => p.id === value.id);
            if (existingIndex !== -1) {
              newFilters.patients.splice(existingIndex, 1);
            } else {
              // Otherwise, add it
              newFilters.patients.push(value);
            }
          }
          break;

        case 'assignee':
          if (value) {
            const existingIndex = newFilters.assignees.findIndex(a => a.id === value.id);
            if (existingIndex !== -1) {
              newFilters.assignees.splice(existingIndex, 1);
            } else {
              newFilters.assignees.push(value);
            }
          }
          break;

        case 'creator':
          if (value) {
            const existingIndex = newFilters.creators.findIndex(c => c.id === value.id);
            if (existingIndex !== -1) {
              newFilters.creators.splice(existingIndex, 1);
            } else {
              newFilters.creators.push(value);
            }
          }
          break;

        case 'tags':
          if (value) {
            const existingIndex = newFilters.tags.indexOf(value);
            if (existingIndex !== -1) {
              newFilters.tags.splice(existingIndex, 1);
            } else {
              newFilters.tags.push(value);
            }
          }
          break;

        case 'repeat':
          // Toggle between repeating, non-repeating, and all
          if (this.activeFilters.showRepeating) {
            newFilters.showRepeating = false;
            newFilters.showNonRepeating = true;
          } else if (this.activeFilters.showNonRepeating) {
            newFilters.showNonRepeating = false;
          } else {
            newFilters.showRepeating = true;
          }
          break;

        case 'due':
          // Set the due date filter to the clicked task's due date
          const clickedDueDate = value ? value : (this.selectedTask ? this.selectedTask.dueAtStr : null);
          if (clickedDueDate) {
            if (this.activeFilters.dueDate === clickedDueDate) {
              // If clicking the same date, clear the filter
              newFilters.dueDate = null;
            } else {
              // Set to the clicked date
              newFilters.dueDate = clickedDueDate;
            }
            // Clear other due-related filters when setting a specific date
            newFilters.dueToday = false;
            newFilters.showOverdue = false;
          } else {
            // Toggle between today, overdue, and all if no specific date
            if (this.activeFilters.dueToday) {
              newFilters.dueToday = false;
              newFilters.showOverdue = true;
            } else if (this.activeFilters.showOverdue) {
              newFilters.showOverdue = false;
            } else {
              newFilters.dueToday = true;
            }
          }
          break;
      }

      // Update filters
      this.handleFilterChange(newFilters);

      // Update TaskFilters component if it exists
      if (this.$refs.taskFilters) {
        this.$refs.taskFilters.selectedTaskFilter = 'Show all tasks';
        if (column === 'patient') {
          this.$refs.taskFilters.selectedPatients = newFilters.patients;
        } else if (column === 'assignee') {
          this.$refs.taskFilters.selectedAssignees = newFilters.assignees;
        } else if (column === 'tags') {
          this.$refs.taskFilters.selectedTags = newFilters.tags;
        } else if (column === 'due') {
          this.$refs.taskFilters.selectedDueDate = newFilters.dueDate;
        }
      }
    },

    // Remove hasActiveFilters method since it's now a computed property

  },

  computed: {
    // ---- Tasks page redesign (design "Tasks 2a", Phase B1 — new branch only) --------------------

    /**
     * Which template branch renders. An office user → STAGE.tasks and their own choice (the old page at "preview"). A Super Admin → the new page unless "Classic view" was picked.
     */
    fuUseNewUi() {
      // eslint-disable-next-line no-unused-expressions
      this.fuUiVersion; // re-read storage after the Classic / New view link
      return shouldUseNewFollowupsUi('tasks', { isSuperAdmin: this.isSuperAdmin, userKey: this.userID, storage: browserStorage() });
    },

    /** Trial: the ShapeShift input in place of the inline TaskComposer — Super Admins who turned it on (src/utils/shapeshiftGate.js). */
    tkShapeShift() {
      // eslint-disable-next-line no-unused-expressions
      this.ssVersion; // re-read storage after the toggle link
      return shouldUseShapeShiftInput({ isSuperAdmin: this.isSuperAdmin, storage: browserStorage() });
    },

    tkLayoutChoices() {
      return [{ key: 'list', label: 'List' }, { key: 'calendar', label: 'Calendar' }, { key: 'workload', label: 'Workload' }];
    },

    /** Super Admin preview: 'list' | 'calendar' | 'workload' (src/utils/tasksLayoutGate.js). Everyone else: 'list', always. */
    tkLayout() {
      // eslint-disable-next-line no-unused-expressions
      this.tkLayoutVersion; // re-read storage after the switch
      return tasksLayout({ isSuperAdmin: this.isSuperAdmin, storage: browserStorage() });
    },

    /** The view the sidebar highlights: outside the list the office-wide views all read as "Everyone". */
    tkSidebarView() {
      return layoutView(this.tkLayout, this.tkView);
    },

    /** A teammate, a tag, "Assigned to me" or "Created by me" narrows the Calendar / Workload exactly as it narrows the list. */
    tkCalendarNarrowed() {
      const v = this.tkSidebarView;
      return !!(this.tkEmp || this.tkTag || v === 'assignedToMe' || v === 'createdByMe');
    },

    /**
     * The OPEN tasks the Calendar / Workload draw: narrowed as above, else the whole office's open tasks
     * (a calendar of "My day" would be one day). The search box applies.
     */
    tkCalendarOpen() {
      if (this.tkCalendarNarrowed) return this.tkListTasks.filter(t => t && t.status !== 'COMPLETED');
      return filterBy(this.tkOpenAll, { q: this.tkQ }, this.tkCtx);
    },

    /**
     * What the Calendar draws: the open tasks, plus — when "Completed" is ticked — the completed ones the
     * same narrowing lists (the site's completed tasks are already loaded for the new page). The Workload
     * is about open work and always takes the open list.
     */
    tkCalendarTasks() {
      if (!this.tkCalShowDone) return this.tkCalendarOpen;
      const me = String(this.userID);
      const v = this.tkSidebarView;
      let done = this.completedTasksForSite || [];
      if (!this.tkEmp && !this.tkTag && v === 'assignedToMe') done = done.filter(t => assigneeIDs(t).some(id => String(id) === me));
      else if (!this.tkEmp && !this.tkTag && v === 'createdByMe') done = done.filter(t => t && String(t.createdByEmpID) === me);
      done = filterBy(done, { empID: this.tkEmp || undefined, tag: this.tkTag || undefined, q: this.tkQ }, this.tkCtx);
      return combineTasks(this.tkCalendarOpen, done);
    },

    tkAddDueLabel() {
      return this.tkAddDue ? moment(this.tkAddDue, 'YYYY-MM-DD').format('ddd, MMM D') : '';
    },

    tkShapeShiftPlaceholder() {
      return this.tkIsMyDay
        ? 'Add to My day — type it the way you’d say it'
        : 'Add a task — type it the way you’d say it';
    },

    tkTimeZone() {
      const s = this.allSettings && this.allSettings.GENERAL_TIMEZONE;
      const tz = s && s.PropertyValue ? String(s.PropertyValue) : '';
      return tz && moment.tz.zone(tz) ? tz : TK_DEFAULT_TIME_ZONE;
    },

    /** myDay.js ctx. "today" is moment's default zone, as the store getter and the old page compare. */
    tkCtx() {
      return {
        userID: this.userID,
        today: moment(this.tkClock).format('YYYY-MM-DD'),
        timeZone: this.tkTimeZone,
        isAdmin: isSiteWideRole(this.user && this.user.Role),
        clean: c => cleanTaskContent(c, typeof document !== 'undefined' ? document : null),
      };
    },

    /** The office's today for the report ("Still open" = due before it). */
    tkReportToday() {
      return moment(this.tkClock).tz(this.tkTimeZone).format('YYYY-MM-DD');
    },

    /**
     * The site's tasks (tasks/getTaskReports → allTasksForSite) as taskReport.js items: due day in the
     * office's date format (DD/MM/YYYY for AFRICA/MASERU — myDay.js dueAtStrFormat), Date / Timestamp
     * values zoned to the office. Only computed while Reports is on screen.
     */
    tkReportItems() {
      if (this.tkScreen !== 'reports') return [];
      const tz = this.tkTimeZone;
      const opts = {
        dueFormat: dueAtStrFormat(tz),
        dayOfInstant: ms => moment(ms).tz(tz).format('YYYY-MM-DD'),
      };
      const list = (this.$store.state.tasks || {}).allTasksForSite;
      return (Array.isArray(list) ? list : []).map(t => normalizeTask(t, opts)).filter(Boolean);
    },

    /** The store lists the old page reads (myDay.js input). */
    tkInput() {
      const t = this.$store.state.tasks || {};
      return {
        myCreated: this.myCreatedTasks,
        assignedToMe: this.assignedToMeTasks,
        myEmployees: this.myEmployeesTasks,
        allSite: t.allTasksForSite,
        completedSite: this.completedTasksForSite,
      };
    },

    tkBusy() {
      return !!(this.busy && !this.tasksLoaded);
    },

    tkIsMyDay() {
      return !this.tkEmp && !this.tkTag && this.tkView === 'myDay' && this.tkLayout === 'list';
    },

    tkRailKey() {
      if (this.tkEmp) return `emp:${this.tkEmp}`;
      if (this.tkTag) return `tag:${this.tkTag}`;
      return this.tkView;
    },

    tkViews() {
      const counts = viewCounts(this.tkInput, this.tkCtx);
      return VIEWS.map(v => ({ key: v.key, label: v.label, count: counts[v.key] || 0 }));
    },

    tkOpenAll() {
      return tasksForView('everyone', this.tkInput, this.tkCtx);
    },

    // "N open" beside each person / patient in the composer's @ and # lists — counted from the same
    // list as the sidebar's Team counts (tkOpenAll), so the two numbers agree.
    tkComposerOpenCounts() {
      return openTaskCounts(this.tkOpenAll);
    },

    tkTeam() {
      const active = (this.employees || []).filter(e => e && (e.Active === undefined || e.Active === 1 || e.Active === true || String(e.EmployeeID) === String(this.userID)));
      return teamCounts(this.tkOpenAll, active, this.tkCtx);
    },

    tkTagRows() {
      return tagCounts(this.tkOpenAll, (this.$store.state.tasks || {}).siteTaskTags);
    },

    tkTeamShown() {
      return TEAM_SHOWN;
    },

    tkTagsShown() {
      return TAGS_SHOWN;
    },

    tkComingUpShown() {
      return COMING_UP_SHOWN;
    },

    // ---- My day ----

    /** Unfiltered — the ring counts the whole day whatever is typed in the search box. */
    tkDayAll() {
      return myDaySections(this.tkInput, this.tkCtx);
    },

    tkDay() {
      if (!this.tkQ.trim()) return this.tkDayAll;
      return sections(filterBy(myDayScope(this.tkInput, this.tkCtx), { q: this.tkQ }, this.tkCtx), this.tkCtx);
    },

    tkProgress() {
      return progress(this.tkDayAll, this.tkCtx);
    },

    tkRingDash() {
      return String(TK_RING_C);
    },

    tkRingOffset() {
      return String(TK_RING_C * (1 - this.tkProgress.pct / 100));
    },

    tkRingTitle() {
      const p = this.tkProgress;
      return p.total && !p.left ? 'All done for today' : `${p.left} left today`;
    },

    /**
     * "3 done since 8:00 AM" only when the time is real: tasks/updateTask stamps completedAt at the
     * start of the office day, so a midnight (or unknown) time means "no time" — then just the count.
     */
    tkRingSub() {
      const p = this.tkProgress;
      if (!p.done) return '';
      if (p.since === null || p.since === undefined || p.since <= 0) return `${p.done} done today`;
      const h = Math.floor(p.since / 60);
      const m = p.since % 60;
      const time = `${h % 12 || 12}:${m < 10 ? '0' : ''}${m} ${h >= 12 ? 'PM' : 'AM'}`;
      return `${p.done} done since ${time}`;
    },

    tkTodaySub() {
      const left = this.tkDay.today.length;
      return left ? `${left} to go` : 'All done';
    },

    tkMovableIds() {
      return moveAllToTodayPatch(this.tkDay.overdue, this.tkCtx).map(p => p.id);
    },

    // ---- The other views, a teammate or a tag ----

    /** The open tasks the current view / pick lists, plus its tasks completed today. */
    tkListTasks() {
      const input = this.tkInput;
      const ctx = this.tkCtx;
      const me = String(this.userID);
      const doneToday = tasksForView('completedToday', input, ctx);
      let list;
      if (this.tkEmp) {
        list = filterBy(combineTasks(this.tkOpenAll, doneToday), { empID: this.tkEmp }, ctx);
      } else if (this.tkTag) {
        list = filterBy(combineTasks(this.tkOpenAll, doneToday), { tag: this.tkTag }, ctx);
      } else {
        const open = tasksForView(this.tkView, input, ctx);
        let done = [];
        if (this.tkView === 'assignedToMe') done = doneToday.filter(t => assigneeIDs(t).some(id => String(id) === me));
        else if (this.tkView === 'createdByMe') done = doneToday.filter(t => String(t.createdByEmpID) === me);
        else if (this.tkView === 'everyone') done = doneToday;
        list = combineTasks(open, done);
      }
      return filterBy(list, { q: this.tkQ }, ctx);
    },

    tkGroups() {
      const g = groupList(this.tkListTasks, this.tkCtx);
      return TK_GROUPS.filter(d => g[d.key] && g[d.key].length).map(d => Object.assign({}, d, { rows: g[d.key] }));
    },

    tkHideSelf() {
      return !this.tkEmp && !this.tkTag && (this.tkView === 'assignedToMe' || this.tkView === 'upcoming');
    },

    tkEmpName() {
      if (!this.tkEmp) return '';
      if (String(this.tkEmp) === String(this.userID)) return 'You';
      const e = (this.employees || []).find(x => x && String(x.EmployeeID) === String(this.tkEmp));
      return e ? e.Name : 'Teammate';
    },

    tkTitle() {
      if (this.tkEmp) return this.tkEmpName;
      if (this.tkTag) return `#${this.tkTag}`;
      if (this.tkLayout !== 'list' && this.tkView !== 'assignedToMe' && this.tkView !== 'createdByMe') {
        return this.tkLayout === 'calendar' ? 'Calendar' : 'Workload';
      }
      const v = VIEWS.find(x => x.key === this.tkView);
      return v ? v.label : 'Tasks';
    },

    tkEyebrow() {
      if (!this.tkIsMyDay) return 'TASKS';
      return moment(this.tkClock).tz(this.tkTimeZone).format('dddd, MMMM D').toUpperCase();
    },

    tkLede() {
      if (this.tkIsMyDay) return 'What’s due today. Anything that slipped waits in its own tray.';
      if (this.tkLayout !== 'list') {
        const n = this.tkCalendarOpen.length;
        return `${n} open ${n === 1 ? 'task' : 'tasks'} shown. Pick a teammate, a tag, Assigned to me or Created by me to narrow it.`;
      }
      const g = groupList(this.tkListTasks, this.tkCtx);
      if (!this.tkEmp && !this.tkTag && this.tkView === 'completedToday') {
        return `${g.done.length} finished today across the practice.`;
      }
      const open = this.tkListTasks.length - g.done.length;
      let s = `${open} open`;
      if (g.overdue.length) s += ` · ${g.overdue.length} overdue`;
      if (g.today.length) s += ` · ${g.today.length} due today`;
      return s;
    },

    tkEmptyText() {
      const q = this.tkQ.trim();
      return q ? `No tasks match “${q}”.` : 'Nothing here right now.';
    },

    /** The inline composer's default due date: start of tomorrow (office zone) on Upcoming, else today. */
    tkComposerDefaultDue() {
      const ctx = this.tkCtx;
      // The calendar's "+" on a day wins; then Upcoming (tomorrow); else today.
      const day = this.tkAddDue || (!this.tkEmp && !this.tkTag && this.tkView === 'upcoming' ? addDays(ctx.today, 1) : ctx.today);
      return moment.tz(day, ctx.timeZone).startOf('day').toDate();
    },

    tkComposerPlaceholder() {
      return this.tkIsMyDay
        ? 'Add to My day — @ to assign, # for a patient'
        : 'Add a task — @ to assign, # for a patient';
    },

    // Removed mapGetters for non-existent 'availableTags'
    computedAvailableTags() {
      // NEW: Merge persistent tags from Firestore with tags from loaded tasks
      const uniqueTags = new Set();
      
      // Priority 1: Get persistent tags from Firestore (task-tags collection)
      const persistentTags = this.$store.state.tasks.siteTaskTags || [];
      persistentTags.forEach(tag => {
        if (typeof tag === 'string' && tag.trim() !== '') {
          uniqueTags.add(tag.trim());
        }
      });
      
      // Priority 2: Add tags from loaded tasks (for backwards compatibility)
      const tasks = this.allTasks || [];
      tasks.forEach(task => {
        // Use the task.tags array directly (should be populated correctly by the store listener)
        if (Array.isArray(task.tags)) {
          task.tags.forEach(tag => {
            if (typeof tag === 'string' && tag.trim() !== '') { // Ensure it's a non-empty string
               uniqueTags.add(tag.trim());
            }
          });
        }
        // Note: We are no longer adding pseudo-tags like "Repeats daily" here.
        // The filter component should handle filtering by repeat status separately if needed.
      });

      return Array.from(uniqueTags).sort(); // Return sorted array
    },
    maxSortIdForNewTask() {
      const max = _.max(_.map(this.todoItems, 'sortID'));
      return Number.isFinite(max) ? max : 0;
    },
    ...mapGetters({
      getNextDueDate: "tasks/getNextDueDate",
    }),

    ...mapState({
         patients: state => state.customer.patients,
         myCreatedTasks: state => state.tasks.myCreatedTasks,
         //myTasksIncludesCreatedByMe: state => state.tasks.myTasksIncludesCreatedByMe,
         assignedToMeTasks: state => state.tasks.assignedToMeTasks,
         myEmployeesTasks: state => state.tasks.myEmployeesTasks,
         tasksLoaded: state => state.tasks.tasksLoaded,
         completedTasksForSite: state => state.tasks.completedTasksForSite, // Map the new state
    }),

    // REMOVED redundant local computed property 'availableTags'
    // The mapped Vuex getter 'availableTags' on line 506 will be used instead.

    myTasksIncludesCreatedByMe: {
      get () {
          if(this.$store.state.tasks.myTasksIncludesCreatedByMe && this.$store.state.tasks.myTasksIncludesCreatedByMe !== null)
            return this.$store.state.tasks.myTasksIncludesCreatedByMe;
          else
            return 'no';
        },
        set (value) { this.setMyTasksIncludesCreatedByMe(value) }
    },

    assignedToMeTasksView: {
        get () {
          if(this.$store.state.tasks.assignedToMeTasksView && this.$store.state.tasks.assignedToMeTasksView !== null)
            return this.$store.state.tasks.assignedToMeTasksView;
          else
            return 'Show all tasks';
        },
        set (value) { this.setAssignedToMeTasksView(value) }
    },

    // Removed unused createdByMeTasksView computed property

    employeeTasksView: {
      get () {
          if(this.$store.state.tasks.employeeTasksView && this.$store.state.tasks.employeeTasksView !== null)
            return this.$store.state.tasks.employeeTasksView
          else
            return 'Show all tasks';
        },
        set (value) { this.setEmployeeTasksView(value); }
    },

    showIntro() {
      const shouldShow = this.tasksLoaded && !this.hasTasks && !this.introDismissed;
      // Show intro ONLY if tasks are loaded, there are no tasks, AND user hasn't dismissed it.
      return shouldShow;
    },

    hasTasks() {
      const hasMyCreatedTasks = this.myCreatedTasks && this.myCreatedTasks.length > 0;
      const hasAssignedToMeTasks = this.assignedToMeTasks && this.assignedToMeTasks.length > 0;
      const hasMyEmployeesTasks = this.myEmployeesTasks && this.myEmployeesTasks.length > 0;

      return hasMyCreatedTasks || hasAssignedToMeTasks || hasMyEmployeesTasks;
    },

    employeesWithShowAll() {
      if(this.activeEmployees) {
        let temp = [...this.activeEmployees];
        let o = {
            Name: 'Show All'
        }
        temp.unshift(o);
        return temp;
      }
    },

    allTasks() {
      // *** NEW: Handle completed tasks filter ***
      if (this.activeFilters.showCompletedOnly) {
        // console.log('Filtering for completed tasks only. Returning completedTasksForSite.');
        // Apply sorting to completed tasks as well
        return this.sortTasksByStatusAndDueDate(this.completedTasksForSite || []);
      }
      // *** END NEW ***

      // Combine other tasks into a single array if not showing completed only
      let tasks = [
        ...(this.myCreatedTasks || []),
        ...(this.assignedToMeTasks || []),
        ...(this.myEmployeesTasks || [])
      ];

      /*
      console.log('Combined tasks count:', tasks.length);
      console.log('myCreatedTasks:', this.myCreatedTasks ? this.myCreatedTasks.length : 0);
      console.log('assignedToMeTasks:', this.assignedToMeTasks ? this.assignedToMeTasks.length : 0);
      console.log('myEmployeesTasks:', this.myEmployeesTasks ? this.myEmployeesTasks.length : 0);
      */

      // Remove duplicates based on task ID
      tasks = tasks.filter((task, index, self) =>
        index === self.findIndex((t) => t.id === task.id)
      );

      // Debug statement removed

      // *** REVISED: Handle single task view ***
      if (this.taskID) {
        console.log('Single task view mode, taskID:', this.taskID);
        const singleTask = this.$store.state.tasks.task;
        // Check if the task in the store matches the requested ID
        if (singleTask && singleTask.id === this.taskID) {
          console.log('Returning single task from store state:', singleTask.id);
          return [singleTask]; // Return only the specific task in an array
        } else {
          console.log(`Task ${this.taskID} not yet loaded or not found in store state.`);
          return []; // Return empty array if not loaded/found
        }
      }
      // *** END REVISED ***

      // Apply general filters (only if no specific taskID)
      tasks = this.filterTasks(tasks);

      // Sort tasks based on the selected sort option (only if no specific taskID)
      const sortedTasks = this.sortTasksByStatusAndDueDate(tasks);

      return sortedTasks;
    },

    isLoading() {
      // Keep this extremely simple to avoid any edge cases
      return this.busy;
    },

    todoItems: {
      get() {
        let tasks = [];
        switch(this.createdByMeTasksView) {
          case 'Show all tasks' : tasks = this.myCreatedTasks; break;
          case 'Show incomplete tasks only': tasks = this.myCreatedTasks.filter(t => t.status === 'INCOMPLETE'); break;
          case 'Show overdue tasks only': tasks = this.myCreatedTasks.filter(t => t.status === 'INCOMPLETE' && moment(t.dueAt).isBefore(moment(), 'day')); break;
          default: tasks = this.myCreatedTasks;
        }

        // Apply filters
        tasks = this.filterTasks(tasks);

        // Sort tasks based on the selected sort option
        return this.sortTasksByStatusAndDueDate(tasks);
      },

      async set(value) {
        if(value) {
          let items = value.map(function(item, index) {
            return { ...item, sortID: index}
          })
          await this.updateMultipleTasks(items);
        }
      }
    },

    assignedToMeTasksFiltered() {
      let temp;

      if(this.myTasksIncludesCreatedByMe && this.myTasksIncludesCreatedByMe === 'no')
        temp = this.assignedToMeTasks.filter(t => t.createdByEmpID !== this.userID);
      else
        temp = this.assignedToMeTasks;

      let tasks = [];
      switch(this.assignedToMeTasksView) {
        case 'Show all tasks':  tasks = temp; break;
        case 'Show incomplete tasks only': tasks = temp.filter(t => t.status === 'INCOMPLETE'); break;
        case 'Show overdue tasks only': tasks = temp.filter(t => t.status === 'INCOMPLETE' && moment(t.dueAt).isBefore(moment(), 'day')); break;
        default: tasks = temp;
      }

      // Apply filters
      tasks = this.filterTasks(tasks);

      // Sort tasks based on the selected sort option
      return this.sortTasksByStatusAndDueDate(tasks);
    },

    hasActiveFilters: {
      get() {
        return (
          this.activeFilters.patients.length > 0 ||
          this.activeFilters.assignees.length > 0 ||
          this.activeFilters.creators.length > 0 ||
          this.activeFilters.tags.length > 0 ||
          this.activeFilters.showIncomplete ||
          this.activeFilters.showOverdue ||
          this.activeFilters.showNewComments ||
          this.activeFilters.showRepeating ||
          this.activeFilters.showNonRepeating ||
          this.activeFilters.showCompletedOnly ||
          this.activeFilters.dueToday ||
          this.activeFilters.dueDate !== null
        );
      },
      set(value) {
        if (!value) {
          this.clearAllFiltersAndReset();
        }
      }
    },
  },

  watch: {
    // ---- Tasks page redesign (new branch only) ----
    // The clock, the narrow-screen check and the completed-tasks listener run only while the new
    // branch is on screen; a non-Super-Admin never starts any of them.
    fuUseNewUi: {
      immediate: true,
      handler(on) {
        if (!on) {
          this.tkStop();
          return;
        }
        this.tkStart();
        this.tkLoadCompleted();
        this.tkEnsureReports();
        this.$nextTick(() => {
          this.tkMaybeOpenDeepLink();
          if (this.tkComposeOnLoad && this.tkFocusComposer()) this.tkComposeOnLoad = false;
        });
      },
    },
    siteID() {
      this.tkLoadCompleted();
      this.tkEnsureReports();
    },
    // ?screen=reports ↔ the list, when the route changes without a remount (Back / Forward, tests).
    '$route.query.screen'(screen) {
      this.tkScreen = screen === 'reports' ? 'reports' : 'list';
      this.tkEnsureReports();
    },
    // The first snapshot after tasks/getTaskReports ends the report's loading state.
    '$store.state.tasks.allTasksForSite'() {
      if (!this.tkReportsWaiting) return;
      this.tkReportsWaiting = false;
      if (tkReportsListener) tkReportsListener.delivered = true;
    },
    taskID() {
      this.tkMaybeOpenDeepLink();
    },

    tasksLoaded(newVal) {
      // If tasks have loaded, make sure busy is set to false
      if (newVal === true) {
        this.busy = false;
      }
      this.tkMaybeOpenDeepLink();
    },
    hasTasks(newVal) {
      // Once a site has tasks, permanently dismiss the intro so it never reappears
      if (newVal === true && !this.introDismissed) {
        this.persistIntroDismissed();
      }
    }
  },
}
</script>

<!-- Tasks page redesign (Phase B1): the new branch's page styles, scoped (`.tk-page`). -->
<style src="./tasks-page-new.scss" lang="scss" scoped />

<style scoped lang="scss">
.dot-before {
    margin: 3px 0;

    &::before {
      content: '•';
      margin-right: 7.5px;
    }
}

li {
  color: #3abf94;
  font-weight: bold;
}

.example {
  color: #000000;
  font-weight: normal;
  margin-left: 15px;
}

.ToDo {
  position: relative;

  &__items {
    margin-top: 0.5rem;
  }

  &__assignedToMe {
    margin-top: 1rem;
  }

  &__myemployeesTask {
    margin-top: 1rem;
  }
}

/* Standardize the dropdown alignment and width across all sections */
.multiselect {
  width: 100%;
  min-width: 180px;
}

:active :focus {
  border-color: #aba3ff; /* Restored original focus style */
}

.task-settings-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  margin-left: 6px;
  margin-right: 4px;
  padding: 0;
  border-radius: 8px;
  background: white;
  border: 1px solid #e2e8f0;
  color: #718096;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #f7fafc;
    color: #3abf94;
    border-color: #3abf94;
  }

  .fa {
    font-size: 14px;
    transition: transform 0.3s ease;
  }

  &:hover .fa {
    transform: rotate(45deg);
  }
}

  .task-report-icon {
    display: flex;
    align-items: center;
    flex: 0 0 auto; /* Don't grow or shrink */
    margin-left: auto; /* Push to the right */

  i {
    font-size: 1.25rem;
    color: #64748b;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 0.375rem;
    transition: all 0.2s ease;

    &:hover {
      color: #3abf94;
      background-color: #f1f5f9;
    }
  }
}

*[contenteditable="true"]{display: inline-block;}

.tooltip-inner {
  padding: -10px;
}

.task-tooltip {
  padding:0px !important;
  padding-left:5px !important;
  padding-bottom:10px !important;
}

// Styles for completed tasks
:deep(.todo-grid--completed) {
  opacity: 0.7 !important;
  background-color: rgba(0, 0, 0, 0.02) !important;
}

:deep(.TodoItem.BaseTodoItem--completed) {
  opacity: 0.7 !important;
  background-color: rgba(0, 0, 0, 0.02) !important;
}

// Modern Intro Card Styles
.teamio-task-intro-card {
  background: #ffffff !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  padding: 0 !important;
  max-width: 1200px !important;
  margin: 1rem auto 2rem !important;
  transition: all 0.3s ease !important;
  display: block !important;
}

.teamio-task-intro-card:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08) !important;
}

.intro-content {
  padding: 2rem;
}

.intro-header {
  text-align: center;
  margin-bottom: 2rem;
}

.intro-title {
  color: #2d3748;
  font-weight: 700;
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
}

.intro-subtitle {
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 400;
}

.intro-body {
  display: flex;
  flex-direction: row;
  gap: 3rem;
  margin-bottom: 2rem;
}

@media (max-width: 992px) {
  .intro-body {
    flex-direction: column;
    gap: 2rem;
  }
}

.intro-image-container {
  flex: 0 0 25%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.intro-image {
  max-width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.06);
}

.intro-features {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.feature-item {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.feature-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
}

.feature-icon {
  font-size: 1.5rem;
  color: #3abf94;
  margin-top: 0.25rem;
}

.feature-text {
  flex: 1;
}

.feature-text h5 {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.feature-text p {
  color: #64748b;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.feature-example {
  font-size: 0.85rem;
  color: #64748b;
  background-color: #f1f5f9;
  padding: 0.5rem;
  border-radius: 4px;
  border-left: 3px solid #3abf94;
}

.feature-example span {
  font-family: monospace;
  font-weight: 500;
  color: #475569;
}

.intro-footer {
  text-align: center;
  margin-top: 1rem;
}

.get-started-btn {
  background-color: #3abf94;
  border-color: #3abf94;
  padding: 0.5rem 1.5rem;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.get-started-btn:hover {
  background-color: #34a884;
  border-color: #34a884;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.task-header {
  display: grid;
  /* RESPONSIVE: Reduced fixed widths to fit most screens */
  grid-template-columns:
    36px             /* 1: Checkbox */
    minmax(180px, 1fr) /* 2: Content - flexible */
    40px             /* 3: Attachments */
    40px             /* 3.5: Subtasks */
    40px             /* 4: Comments */
    140px            /* 5: Patients */
    85px             /* 6: Status */
    100px            /* 7: Tags */
    110px            /* 8: Repeats */
    85px             /* 9: Due Date */
    95px             /* 10: Creator */
    115px            /* 11: Assignees - increased from 95px to fit +N badge */
    36px;            /* 12: Actions */
  padding: 0.375rem 0.5rem;
  margin-bottom: 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.75rem;
  color: #475569;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  align-items: center;
  column-gap: 6px; /* Reduced gap */
  background-color: white;
  min-width: 1100px; /* Desktop minimum width */
  max-width: 100%; /* Prevent overflow */
  height: 34px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  .sortable {
    cursor: pointer;
    user-select: none;
    transition: color 0.2s ease;

    &:hover {
      color: #3abf94;
    }

    .fa-sort {
      margin-left: 4px;
      font-size: 12px;
      opacity: 0.5;
    }

    &:hover .fa-sort {
      opacity: 1;
    }
  }

  > div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 2px;
  }

  .header-checkbox {
    width: 36px;
    justify-self: center;
    text-align: center;
  }

  .header-description {
    justify-self: start;
    padding-left: 0.15rem;
    font-weight: 600;
  }

  .header-icon {
    font-size: 0.75rem;
    color: #64748b;
  }

  .header-tags {
    max-width: 100px;
    justify-self: start;
    display: flex;
    align-items: center;
    color: #0f172a;
    font-weight: 600;
  }

  .header-patients {
    min-width: 100px;
    justify-self: start;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    color: #0f172a;
    font-weight: 600;
    padding-left: 0;
  }

  .header-status {
    min-width: 70px;
    justify-self: center;
    text-align: center;
  }

  .header-attachment,
  .header-subtasks {
    width: 36px;
    justify-self: center;
    text-align: center;
  }

  .header-comments {
    width: 36px;
    justify-self: center;
    text-align: center;
  }

  .header-date {
    min-width: 70px;
    justify-self: center;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0f172a;
    font-weight: 600;
  }

  .header-creator {
    min-width: 80px;
    justify-self: start;
    text-align: left;
    display: flex;
    align-items: center;
    color: #0f172a;
    font-weight: 600;
    padding-left: 0.15rem;
  }

  .header-assignee {
    min-width: 0;
    justify-self: start;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    color: #0f172a;
    font-weight: 600;
    padding-left: 0.25rem;
    gap: 3px;
  }

  .header-actions {
    width: 36px;
    justify-self: flex-end;
    text-align: right;
    font-size: 0;
  }
}

.header-spacer {
  height: 4px;
}

/* Horizontal scroll container for task list */
.tasks-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  max-width: 100%; /* Prevent container from exceeding viewport */
  box-sizing: border-box; /* Include padding/border in width calculation */
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
}

.ToDo__items {
  min-width: 1100px; /* Desktop default - ensure items don't compress below readable width */
  width: 100%;
  max-width: 100%;
}

/* Media query for responsive layout - hide less important columns progressively */
@media (max-width: 1400px) {
  .task-header {
    grid-template-columns:
      36px             /* 1: Checkbox */
      minmax(160px, 1fr) /* 2: Content */
      36px             /* 3: Attachments */
      36px             /* 3.5: Subtasks */
      36px             /* 4: Comments */
      120px            /* 5: Patients */
      80px             /* 6: Status */
      90px             /* 7: Tags */
      100px            /* 8: Repeats */
      80px             /* 9: Due Date */
      90px             /* 10: Creator */
      110px            /* 11: Assignees - increased from 90px */
      36px;            /* 12: Actions */
    column-gap: 4px;
    min-width: 1000px;
  }
}

@media (min-width: 1025px) and (max-width: 1200px) {
  .task-header,
  .TodoItem {
    /* Hide Comments column at this breakpoint */
    grid-template-columns:
      36px             /* 1: Checkbox */
      minmax(150px, 1fr) /* 2: Content */
      36px             /* 3: Attachments */
      36px             /* 3.5: Subtasks */
      /* 4: Comments - hidden */
      110px            /* 5: Patients */
      75px             /* 6: Status */
      85px             /* 7: Tags */
      95px             /* 8: Repeats */
      75px             /* 9: Due Date */
      85px             /* 10: Creator */
      105px            /* 11: Assignees - increased from 85px */
      36px;            /* 12: Actions */
    column-gap: 4px;
    min-width: 900px;
  }
  
  .header-comments,
  .column-comments,
  .BaseTodoItem__col--comments {
    display: none !important;
  }
}

/* ============================================ */
/* TABLE GRID RESPONSIVE - Progressive column hiding */
/* No overlapping breakpoints — each range is exclusive */
/* ============================================ */

/* ============================================ */
/* TABLET + PHONE CARD LAYOUT (<= 1024px) */
/* Hide table header — cards are self-describing */
/* ============================================ */
@media (max-width: 1024px) {
  /* Hide column headers — cards are self-describing */
  .task-header {
    display: none !important;
  }

  .header-spacer {
    display: none !important;
  }

  .ToDo__items {
    min-width: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 2px;
    overflow: hidden;
  }

  /* Card container — clean background, no table borders */
  .tasks-scroll-container {
    border: none;
    border-radius: 0;
    overflow-x: hidden;
    overflow-y: visible;
    background: transparent;
    padding: 4px 0;
    width: 100%;
    max-width: 100%;
  }
}

/* ============================================ */
/* TABLET & IPAD RESPONSIVE STYLES (768-1024px) */
/* ============================================ */
@media (min-width: 768px) and (max-width: 1024px) {
  /* Ensure main content area doesn't overflow */
  .ToDo {
    max-width: 100%;
    overflow-x: hidden;
  }

  .task-report-icon {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    margin-left: 0;
  }

  /* Task Controls - wrap naturally on tablet */
  .task-controls {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    max-width: 100%;
  }

  .task-filters-box {
    flex: 1 1 100% !important;
    width: 100% !important;
  }

  .quick-actions-section {
    flex: 0 0 auto;
    margin-top: 0;
    margin-bottom: 0;
  }

  .quick-filters {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
  }

  .quick-action-button {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .task-search-box {
    flex: 1 1 auto;
    max-width: 280px !important;
    min-width: 180px;
  }

  /* Show All Tasks Link */
  .show-all-tasks-link {
    width: 100%;
    margin-bottom: 8px;
  }
}

/* ============================================ */
/* MOBILE RESPONSIVE STYLES (< 768px) */
/* ============================================ */
@media (max-width: 767px) {
  .task-report-icon {
    display: flex !important;
    flex: 0 0 auto;
    align-items: center;
    margin-left: 0;
  }

  /* Task Controls - Full width stacking on mobile */
  .task-controls {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }

  .task-filters-box {
    width: 100%;
  }

  .quick-actions-section {
    width: 100%;
  }

  .quick-filters {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    gap: 8px;
  }

  .quick-action-button {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
    display: flex;
    justify-content: center;
    font-size: 12px;
    padding: 6px 8px !important;
  }

  .task-search-box {
    width: 100%;
    max-width: 100%;
  }

  .show-all-tasks-link {
    width: 100%;
    text-align: center;
    margin-bottom: 8px;
  }
}

/* ============================================ */
/* SMALL PHONE STYLES (< 480px) */
/* ============================================ */
@media (max-width: 480px) {
  .quick-action-button {
    font-size: 11px;
    padding: 5px 6px !important;
  }
}

/* ============================================ */
/* DESKTOP STYLES (> 1024px) - Optimize layout */
/* ============================================ */
@media (min-width: 1025px) {
  /* Desktop already uses row layout by default, just ensure proper spacing */
  .task-controls {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 16px;
  }

  .task-filters-box {
    flex: 1 1 auto;
    min-width: 400px;
  }

  .quick-actions-section {
    flex: 0 0 auto;
  }

  .task-search-box {
    flex: 0 0 auto;
    width: 280px;
    max-width: 280px;
  }
}

/* =====================================================
   MODERN EMPTY STATE - Matching PracticePulse/Huddle style
   ===================================================== */
$brand-green: #3abf94;

.tasks-empty-state-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 40vh;
  padding: 20px;
}

.tasks-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 480px;

  // Detailed variant for the intro/onboarding empty state
  &.tasks-empty-state--detailed {
    max-width: 900px;
    background: #fff;
    border-radius: 16px;
    padding: 28px 32px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;

    .empty-state-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 24px;

      .empty-state-visual {
        width: 72px;
        height: 72px;
        margin-bottom: 16px;

        .visual-icon {
          inset: 18px;
        }
      }

      .empty-state-title {
        font-size: 1.375rem;
        margin-bottom: 6px;
      }

      .empty-state-text {
        margin-bottom: 0;
        max-width: 500px;
      }
    }
  }

  // Top CTA button — sits right after the header text, above the fold
  .empty-state-actions--top {
    margin-top: 16px;
    margin-bottom: 0;
  }

  .empty-state-visual {
    position: relative;
    width: 140px;
    height: 140px;
    margin-bottom: 32px;

    .visual-circles {
      position: absolute;
      inset: 0;

      .circle {
        position: absolute;
        border-radius: 50%;
        border: 2px solid rgba(58, 191, 148, 0.15);
        animation: pulse-ring 3s ease-out infinite;

        &.circle-1 {
          inset: 0;
          animation-delay: 0s;
        }
        &.circle-2 {
          inset: 15px;
          animation-delay: 0.5s;
        }
        &.circle-3 {
          inset: 30px;
          animation-delay: 1s;
        }
      }
    }

    .visual-icon {
      position: absolute;
      inset: 40px;
      background: linear-gradient(135deg, #3abf94 0%, darken(#3abf94, 10%) 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 8px 24px rgba(58, 191, 148, 0.35);

      i {
        font-size: 1.75rem;
        color: #fff;
      }

      &.filter-icon {
        background: linear-gradient(135deg, #64748b 0%, #475569 100%);
        box-shadow: 0 8px 24px rgba(100, 116, 139, 0.35);
      }
    }
  }

  .empty-state-title {
    font-size: 1.375rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 12px 0;
  }

  .empty-state-text {
    font-size: 0.9375rem;
    color: #64748b;
    line-height: 1.6;
    margin: 0 0 28px 0;
  }

  // Feature cards grid for detailed empty state
  .empty-state-features-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 20px;
    width: 100%;

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }

    .feature-card {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      text-align: left;
      padding: 16px;
      background: #f8fafc;
      border-radius: 12px;
      border: 1px solid #e2e8f0;
      transition: all 0.2s ease;

      &:hover {
        border-color: rgba(58, 191, 148, 0.3);
        box-shadow: 0 4px 16px rgba(58, 191, 148, 0.12);
        transform: translateY(-2px);
      }

      .feature-icon {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 14px;

        i {
          font-size: 1.25rem;
          color: #fff;
        }

        &.feature-icon--success {
          background: linear-gradient(135deg, #3abf94 0%, #2da67f 100%);
          box-shadow: 0 4px 12px rgba(58, 191, 148, 0.3);
        }

        &.feature-icon--primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        &.feature-icon--info {
          background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
          box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3);
        }

        &.feature-icon--warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
        }
      }

      .feature-content {
        flex: 1;

        .feature-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 6px 0;
        }

        .feature-desc {
          font-size: 0.875rem;
          color: #64748b;
          line-height: 1.5;
          margin: 0 0 12px 0;

          code {
            background: rgba(58, 191, 148, 0.12);
            color: #3abf94;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 0.875rem;
            font-weight: 600;
          }
        }

        .feature-example {
          background: #fff;
          padding: 10px 12px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          font-size: 0.8125rem;

          .example-label {
            color: #94a3b8;
            font-weight: 500;
            margin-right: 4px;
          }

          .example-text {
            color: #475569;

            strong {
              color: #3abf94;
              font-weight: 600;
            }
          }
        }
      }
    }
  }

  // Simple feature chips for minimal empty state
  .empty-state-features {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
    margin-bottom: 32px;

    .feature-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 18px;
      background: #fff;
      border-radius: 10px;
      font-size: 0.875rem;
      font-weight: 500;
      color: #334155;
      border: 1px solid #e2e8f0;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
      transition: all 0.2s ease;

      &:hover {
        border-color: rgba(58, 191, 148, 0.3);
        box-shadow: 0 4px 12px rgba(58, 191, 148, 0.12);
        transform: translateY(-2px);
      }

      i {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, rgba(58, 191, 148, 0.15) 0%, rgba(58, 191, 148, 0.08) 100%);
        border-radius: 6px;
        color: #3abf94;
        font-size: 0.8rem;
      }

      span {
        font-weight: 500;
      }
    }
  }

  // Actions section
  .empty-state-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
  }

  .empty-state-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    font-size: 0.9375rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;

    &.empty-state-btn--primary {
      background: #3abf94;
      color: #fff;

      &:hover {
        background: darken(#3abf94, 8%);
        box-shadow: 0 6px 20px rgba(58, 191, 148, 0.4);
        transform: translateY(-2px);
      }
    }

    &.empty-state-btn--secondary {
      background: #f1f5f9;
      color: #475569;

      &:hover {
        background: #e2e8f0;
        transform: translateY(-2px);
      }
    }

    // Legacy support for old button without modifier
    &:not(.empty-state-btn--primary):not(.empty-state-btn--secondary) {
      background: #3abf94;
      color: #fff;

      &:hover {
        background: darken(#3abf94, 8%);
        box-shadow: 0 6px 20px rgba(58, 191, 148, 0.4);
        transform: translateY(-2px);
      }
    }
  }
}

@keyframes pulse-ring {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1.3);
  }
}

/* Legacy No Results Styles (keeping for backwards compatibility) */
.no-results-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 250px;
  border-radius: 8px;
  background-color: #f8fafc;
  margin-top: 1rem;
  border: 1px dashed #cbd5e1;
}

.no-results-content {
  text-align: center;
  padding: 2rem;
  max-width: 500px;
}

.no-results-icon {
  font-size: 3.5rem;
  color: #cbd5e1;
  margin-bottom: 1rem;
}

.no-results-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.75rem;
}

.no-results-text {
  font-size: 1rem;
  color: #64748b;
  line-height: 1.5;
}

.clear-all-button {
  background: none;
  border: none;
  color: #3abf94;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s ease;
  /* Ensure the button is clickable */
  pointer-events: auto !important;
}

.clear-all-button:hover {
  color: #2c9d78;
  text-decoration: underline;
}

/* Task Controls Styles */
.task-controls {
  display: flex;
  flex-direction: row; /* Horizontal on desktop */
  flex-wrap: wrap; /* Allow wrapping */
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
  width: 100%;
  position: relative;
  z-index: 5;
}

.task-filters-box {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.task-search-wrapper {
  position: relative;
  z-index: 100;
}

.task-search-box {
  width: 100%;
  max-width: 400px;
  flex-shrink: 0;
  position: relative;
  z-index: 25;
}

/* Add these classes to ensure the search input is always clickable */
.ais-InstantSearch,
.ais-SearchBox,
.ais-SearchBox-form,
.ais-SearchBox-input {
  pointer-events: auto !important;
}

/* Fix z-index issue with the search results dropdown */
.task-search-container {
  position: relative;
  z-index: 100;
}

/* Ensure search results appear above other elements */
.search-results {
  z-index: 1000;
}

/* Add these styles for the debug info section */
.debug-info {
  background-color: #fefcbf; /* Light yellow background */
  border: 1px solid #fde047; /* Yellow border */
  border-radius: 6px;
  padding: 12px 15px;
  margin-top: 10px;
  margin-bottom: 15px;
  font-family: monospace;
  font-size: 13px;
  color: #713f12; /* Darker text for readability */
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.debug-title {
  font-size: 14px;
  font-weight: 600;
  color: #854d0e; /* Slightly darker title */
  margin-bottom: 8px;
  border-bottom: 1px solid #facc15; /* Separator line */
  padding-bottom: 5px;
}

.debug-content {
  background-color: #fffbeb; /* Slightly lighter pre background */
  padding: 10px;
  border-radius: 4px;
  white-space: pre-wrap; /* Wrap long lines */
  word-break: break-all; /* Break long words */
  max-height: 200px; /* Limit height */
  overflow-y: auto; /* Add scroll if needed */
}

/* Add these new styles for the overlay */
.overlay-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  background-color: rgba(255, 255, 255, 0.85); /* Semi-transparent white background */
  padding: 2rem 3rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px); /* Add a blur effect */
  -webkit-backdrop-filter: blur(5px); /* For Safari */
}

.spinner-container {
  margin-bottom: 1rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3em;
  color: #3abf94; /* Use theme color */
}

.loading-text {
  margin-top: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: #334155; /* Darker text color */
}

/* Ensure overlay covers the content */
.b-overlay-wrap .b-overlay .bg-light {
  background-color: rgba(248, 250, 252, 0.6) !important; /* Adjust main overlay background */
}

/* Repeats Header Style */
.header-repeats {
  justify-self: start;
  min-width: 90px;
  max-width: 110px;
}

/* Quick Actions Styles */
.quick-actions-section {
  /* Removed border, padding, background */
  margin-bottom: 1rem; /* Keep space below */
}

/* Removed .quick-actions-label style */

.quick-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap; /* Allow wrapping on smaller screens */
  margin-bottom: 0.25rem; /* Match filter-group margin */
}

.quick-action-button {
  padding: 0.4rem 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  height: 34px; /* Match filter input heights */
  transition: all 0.2s ease-in-out;
  flex: 0 0 auto; /* Don't grow or shrink */
  white-space: nowrap; /* Prevent text wrapping */
  display: inline-flex;
  align-items: center;
  border-radius: 6px;

  i {
    font-size: 0.875rem;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
}
</style>

<style scoped>
/* Search results styles */

.searchresults {
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1000;
  position: fixed;
  background-color: #fff;
}

.MyCustomTasksHits1 {
  z-index: 1000;
  position: fixed;
  background-color: #f9fbfd;
}

.MyCustomCommentsHits {
  margin-top: 20px;
}

.MyCustomHitsItem {
  width: 91%;
  cursor: pointer;
}
</style>

<style>
.date-preview-container {
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px 15px;
  background-color: #f0f9f4;
  border-radius: 6px;
  border-left: 4px solid #3abf94;
  transition: all 0.2s ease;
  animation: fadeIn 0.3s ease-in-out;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.date-preview-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-preview-icon {
  color: #3abf94;
  font-size: 18px;
}

.date-preview-text {
  font-size: 14px;
  color: #2d3748;
  flex-grow: 1;
}

.date-preview-check {
  color: #22c55e;
  font-size: 16px;
  margin-left: auto;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

.task-input-container {
  position: relative;
}

.date-preview {
  position: absolute;
  left: 0;

  top: 100%;
  margin-top: 5px;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.ToDo__addNew {
  min-height: 38px;
  margin-bottom: 5px;
}
</style>
